import "emoji-picker-element";
import { Editor, Extension, Node, mergeAttributes } from "@tiptap/core";
import { Fragment } from "@tiptap/pm/model";
import { EditorState, NodeSelection, Plugin, TextSelection } from "@tiptap/pm/state";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import { Table } from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import TextAlign from "@tiptap/extension-text-align";
import { resolveLinkPresentation } from "../utils/html.js";
import { showSuccessNotification } from "../ui/notification.js";
import { ChecklistWrapDecoration } from "./checklistWrapDecoration.js";
import { mountProgramRoadmap } from "../docs/program-roadmap/host.js";

const EMPTY_DOC = {
    type: "doc",
    content: [
        {
            type: "paragraph"
        }
    ]
};

const CALLOUT_TYPES = new Set(["info", "success", "warning", "danger", "neutral"]);
const STATUS_CHIP_TONES = new Set(["grey", "blue", "green", "amber", "red", "purple"]);
const DOCS_EMOJI_DATA_SOURCE = "/public/emoji/data.json";
const DOCS_IMAGE_TAG = "img[data-doc-image]";
const DOCS_IMAGE_MIN_WIDTH = 160;
const DOCS_IMAGE_DEFAULT_WIDTH = 720;
const YOUTUBE_EMBED_HOST = "https://www.youtube.com/embed/";
const CALLOUT_ICON_PATHS = {
    info: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z",
    success: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14l-4-4 1.41-1.41L10 13.17l6.59-6.59L18 8l-8 8z",
    warning: "M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z",
    danger: "M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z"
};

const DOCS_LINK_URL_REGEX = /(\b(?:https?|ftp):\/\/[^\s<>"]+|#(?:project-\d+|task-\d+|tasks\?task=[^\s<>"]+|docs\?doc=[^\s<>"]+))/gi;
const DOCS_LINK_URL_AT_END_REGEX = /(\b(?:https?|ftp):\/\/[^\s<>"]+|#(?:project-\d+|task-\d+|tasks\?task=[^\s<>"]+|docs\?doc=[^\s<>"]+))$/i;

let docsLinkPresentationContextResolver = () => ({});
let docsFileAttachmentResolver = () => ({});
let docsFileUploadHandler = null;

export function setDocsLinkPresentationContextResolver(resolver) {
    docsLinkPresentationContextResolver = typeof resolver === "function" ? resolver : () => ({});
}

export function setDocsFileAttachmentResolver(resolver) {
    docsFileAttachmentResolver = typeof resolver === "function" ? resolver : () => ({});
}

export function setDocsFileUploadHandler(handler) {
    docsFileUploadHandler = typeof handler === "function" ? handler : null;
}

function getDocsLinkPresentationContext() {
    try {
        return docsLinkPresentationContextResolver?.() || {};
    } catch {
        return {};
    }
}

function resolveDocsFileAttachment(attrs = {}) {
    try {
        return docsFileAttachmentResolver?.(attrs) || {};
    } catch {
        return {};
    }
}

function buildDocsLinkChipAttrs(href, fallbackLabel = "") {
    const normalizedHref = String(href || "").trim();
    const presentation = resolveLinkPresentation(normalizedHref, getDocsLinkPresentationContext()) || null;
    return {
        href: presentation?.href || normalizedHref,
        label: presentation?.displayLabel || String(fallbackLabel || normalizedHref),
        variant: presentation?.variant || "external"
    };
}

const DocsLinkChip = Node.create({
    name: "linkChip",
    group: "inline",
    inline: true,
    atom: true,
    selectable: false,

    addAttributes() {
        return {
            href: { default: "" },
            label: { default: "" },
            variant: { default: "external" }
        };
    },

    parseHTML() {
        return [
            {
                tag: "a[data-link-chip]",
                getAttrs: (element) => buildDocsLinkChipAttrs(
                    element.getAttribute("href") || "",
                    element.getAttribute("data-link-label") || element.textContent || ""
                )
            }
        ];
    },

    renderHTML({ HTMLAttributes }) {
        const variant = HTMLAttributes.variant || "external";
        const label = HTMLAttributes.label || HTMLAttributes.href || "";
        const href = HTMLAttributes.href || "";
        const attrs = mergeAttributes(
            {
                href,
                "data-link-chip": "true",
                "data-link-label": label,
                "data-link-variant": variant,
                class: `docs-internal-link docs-internal-link--${variant} description-link-chip-node`,
                contenteditable: "false",
                spellcheck: "false"
            },
            HTMLAttributes
        );

        if (variant === "task") {
            delete attrs.target;
            delete attrs.rel;
        } else {
            attrs.target = "_blank";
            attrs.rel = "noopener noreferrer";
        }

        return ["a", attrs, label];
    },

    addProseMirrorPlugins() {
        return [
            new Plugin({
                appendTransaction(transactions, _oldState, newState) {
                    if (!transactions.some(tr => tr.docChanged)) return null;
                    const ATOM_NODES = ["linkChip", "statusChip", "docsFileChip", "docsFileCard"];
                    let tr = null;
                    newState.doc.descendants((node, pos) => {
                        if (!ATOM_NODES.includes(node.type.name)) return;
                        const afterPos = pos + node.nodeSize;
                        const $after = newState.doc.resolve(afterPos);
                        const nodeAfter = $after.nodeAfter;
                        // If chip is last in its block or followed by another atom, ensure a trailing space
                        if (!nodeAfter || ATOM_NODES.includes(nodeAfter.type.name)) {
                            if (!tr) tr = newState.tr;
                            tr.insertText(" ", tr.mapping.map(afterPos));
                        }
                    });
                    return tr;
                }
            })
        ];
    }
});

function getCalloutIconSpec(tone) {
    const path = CALLOUT_ICON_PATHS[tone];
    if (!path) return null;
    return [
        "svg",
        { viewBox: "0 0 24 24", fill: "currentColor" },
        ["path", { d: path }]
    ];
}

function findAncestorNodeDepth($pos, nodeNames) {
    for (let depth = $pos.depth; depth > 0; depth -= 1) {
        if (nodeNames.has($pos.node(depth)?.type?.name)) {
            return depth;
        }
    }
    return -1;
}

function interpolateDocsText(template, vars = null) {
    const source = String(template || "");
    if (!vars || typeof vars !== "object") return source;
    return source.replace(/\{(\w+)\}/g, (match, name) => {
        if (Object.prototype.hasOwnProperty.call(vars, name)) {
            return String(vars[name]);
        }
        return match;
    });
}

function docsT(key, fallback, vars = null) {
    if (typeof window !== "undefined" && window.i18n && typeof window.i18n.t === "function") {
        const translated = window.i18n.t(key, vars || undefined);
        if (translated && translated !== key) {
            return interpolateDocsText(translated, vars);
        }
    }
    return interpolateDocsText(fallback, vars);
}

function normalizeDocsFileAttrs(attrs = {}) {
    const rawSize = Number(attrs?.size);
    return {
        fileKey: String(attrs?.fileKey || "").trim(),
        name: String(attrs?.name || "").trim(),
        mimeType: String(attrs?.mimeType || "").trim(),
        size: Number.isFinite(rawSize) && rawSize > 0 ? Math.round(rawSize) : 0,
        fileClass: String(attrs?.fileClass || "file").trim() || "file",
        extension: String(attrs?.extension || "").trim().replace(/[^a-z0-9]/gi, "").toLowerCase()
    };
}

function formatDocsFileSize(size) {
    const value = Number(size);
    if (!Number.isFinite(value) || value <= 0) return "";
    if (value < 1024) return `${value} B`;
    if (value < 1024 * 1024) return `${(value / 1024).toFixed(value >= 10 * 1024 ? 0 : 1)} KB`;
    if (value < 1024 * 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(value >= 100 * 1024 * 1024 ? 0 : 1)} MB`;
    return `${(value / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function getDocsFilePresentation(attrs = {}) {
    const normalized = normalizeDocsFileAttrs(attrs);
    const resolved = resolveDocsFileAttachment(normalized);
    const name = String(resolved.name || normalized.name || docsT("docs.fileUntitled", "Untitled file")).trim() || docsT("docs.fileUntitled", "Untitled file");
    const fileClass = String(resolved.fileClass || normalized.fileClass || "file").trim() || "file";
    const extension = String(resolved.extension || normalized.extension || "").trim().replace(/[^a-z0-9]/gi, "").toLowerCase();
    const size = Number.isFinite(Number(resolved.size)) ? Number(resolved.size) : normalized.size;
    const typeLabel = String(
        resolved.typeLabel
        || (extension ? extension.toUpperCase() : fileClass.toUpperCase())
        || "FILE"
    ).trim();
    const sizeLabel = String(resolved.sizeLabel || formatDocsFileSize(size)).trim();
    const badgeLabel = String(
        resolved.badgeLabel
        || (extension ? extension.toUpperCase() : typeLabel)
        || "FILE"
    ).slice(0, 4).trim() || "FILE";
    const previewable = resolved.previewable === true;
    const openUrl = String(resolved.openUrl || "").trim();
    const downloadUrl = String(resolved.downloadUrl || openUrl || "").trim();

    return {
        ...normalized,
        name,
        fileClass,
        extension,
        size,
        typeLabel,
        sizeLabel,
        badgeLabel,
        previewable,
        openUrl,
        downloadUrl,
        primaryActionLabel: String(
            resolved.primaryActionLabel
            || (previewable ? docsT("docs.fileOpen", "Open") : docsT("docs.fileDownload", "Download"))
        ).trim()
    };
}

function getActiveDocsFileSelection(state) {
    const { selection } = state;
    if (selection?.node?.type?.name === "docsFileChip" || selection?.node?.type?.name === "docsFileCard") {
        return {
            node: selection.node,
            pos: selection.from
        };
    }
    return null;
}

function openDocsFilePresentation(presentation, forceDownload = false) {
    const url = forceDownload
        ? presentation.downloadUrl
        : (presentation.previewable ? presentation.openUrl : presentation.downloadUrl);
    if (!url || typeof document === "undefined") return;

    const anchor = document.createElement("a");
    anchor.href = url;
    if (forceDownload || !presentation.previewable) {
        anchor.download = presentation.name || "";
    } else {
        anchor.target = "_blank";
        anchor.rel = "noopener noreferrer";
    }
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
}

function replaceDocsFileCardWithChip(editor, pos, node) {
    const chipType = editor.state.schema.nodes.docsFileChip;
    if (!chipType) return false;

    const { state } = editor;
    const chipNode = chipType.create(normalizeDocsFileAttrs(node.attrs));
    const tr = state.tr.replaceWith(pos, pos + node.nodeSize, chipNode);
    // Replacing a block card with an inline chip wraps the chip in a paragraph,
    // so the chip lands at pos+1, not pos. Scan nearby positions to find it.
    let targetPos = null;
    tr.doc.nodesBetween(
        Math.max(0, pos - 1),
        Math.min(tr.doc.content.size, pos + chipNode.nodeSize + 3),
        (n, p) => { if (n.type === chipType && targetPos === null) targetPos = p; }
    );
    if (targetPos !== null && tr.doc.nodeAt(targetPos)) {
        tr.setSelection(NodeSelection.create(tr.doc, targetPos));
    }
    editor.view.dispatch(tr);
    return true;
}

function replaceDocsFileChipWithCard(editor, pos, node) {
    const cardType = editor.state.schema.nodes.docsFileCard;
    if (!cardType) return false;

    const { state } = editor;
    const cardNode = cardType.create(normalizeDocsFileAttrs(node.attrs));
    // The trailing-space plugin appends a space text node immediately after every
    // inline atom, so the chip's paragraph looks like: <p><chip/> </p>.
    // Include that trailing space in the replaced range so ProseMirror doesn't
    // split the paragraph and leave a stray <p> </p> after the card.
    const $pos = state.doc.resolve(pos);
    const parentNode = $pos.parent;
    const chipEndOffset = $pos.parentOffset + node.nodeSize; // offset inside parent
    let replaceEnd = pos + node.nodeSize;
    if (chipEndOffset < parentNode.content.size) {
        const nodeAfterChip = parentNode.nodeAt(chipEndOffset);
        if (nodeAfterChip && nodeAfterChip.isText && /^\s+$/.test(nodeAfterChip.text)) {
            replaceEnd += nodeAfterChip.nodeSize;
        }
    }
    const tr = state.tr.replaceWith(pos, replaceEnd, cardNode);
    // Replacing an inline chip with a block card causes ProseMirror to restructure
    // the paragraph (splitting it), so the card may land at a different position
    // than pos. Scan nearby positions to find where the card actually landed.
    let targetPos = null;
    tr.doc.nodesBetween(
        Math.max(0, pos - 2),
        Math.min(tr.doc.content.size, pos + cardNode.nodeSize + 4),
        (n, p) => { if (n.type === cardType && targetPos === null) targetPos = p; }
    );
    if (targetPos !== null && tr.doc.nodeAt(targetPos)) {
        tr.setSelection(NodeSelection.create(tr.doc, targetPos));
    }
    editor.view.dispatch(tr);
    return true;
}

function convertDocsFileSelection(editor, display = "card") {
    const activeFile = getActiveDocsFileSelection(editor.state);
    if (!activeFile) return false;
    if (display === "card" && activeFile.node.type.name === "docsFileChip") {
        return replaceDocsFileChipWithCard(editor, activeFile.pos, activeFile.node);
    }
    if (display === "chip" && activeFile.node.type.name === "docsFileCard") {
        return replaceDocsFileCardWithChip(editor, activeFile.pos, activeFile.node);
    }
    return false;
}

function normalizeYoutubeEmbed(value) {
    const raw = String(value || "").trim();
    if (!raw) return null;

    let url;
    try {
        url = raw.startsWith("http://") || raw.startsWith("https://")
            ? new URL(raw)
            : new URL(`https://${raw}`);
    } catch {
        return null;
    }

    const host = url.hostname.replace(/^www\./, "").toLowerCase();
    let videoId = "";

    if (host === "youtu.be") {
        videoId = url.pathname.split("/").filter(Boolean)[0] || "";
    } else if (host === "youtube.com" || host === "m.youtube.com" || host === "music.youtube.com") {
        if (url.pathname === "/watch") {
            videoId = url.searchParams.get("v") || "";
        } else if (url.pathname.startsWith("/shorts/") || url.pathname.startsWith("/embed/")) {
            videoId = url.pathname.split("/").filter(Boolean)[1] || "";
        }
    }

    videoId = String(videoId || "").replace(/[^a-zA-Z0-9_-]/g, "");
    if (!videoId) return null;

    return {
        videoId,
        src: `${YOUTUBE_EMBED_HOST}${videoId}`
    };
}

const PreventTableEdgeDelete = Node.create({
    name: "preventTableEdgeDelete",
    priority: 1000,

    addProseMirrorPlugins() {
        return [
            new Plugin({
                props: {
                    handleKeyDown(view, event) {
                        if (event.key !== "Delete") return false;

                        const { selection } = view.state;
                        if (!selection.empty) return false;

                        const cellDepth = findAncestorNodeDepth(
                            selection.$from,
                            new Set(["tableCell", "tableHeader"])
                        );
                        if (cellDepth < 0) return false;

                        // Prevent forward-delete from crossing a table-cell boundary.
                        // In the last cell of a row, ProseMirror may merge with the next row,
                        // which is the destructive behavior we want to block.
                        // `endOfTextblock("forward")` catches the real caret position the user has:
                        // after the last visible character in the current text block.
                        if (!view.endOfTextblock("forward")) return false;

                        // Only block if the current block is the LAST child of its parent.
                        // If there is a next sibling, Delete merges two paragraphs within the
                        // same cell — that is intentional and should be allowed.
                        const $from = selection.$from;
                        const indexInParent = $from.index($from.depth - 1);
                        const siblingCount = $from.node($from.depth - 1).childCount;
                        if (indexInParent < siblingCount - 1) return false;

                        event.preventDefault();
                        return true;

                        return false;
                    }
                }
            })
        ];
    }
});

// Sorting state belongs to the header cell so it is saved with the document and
// naturally survives Publish/reload. The actual sorted order is the row order.
const SortableTableHeader = TableHeader.extend({
    addAttributes() {
        return {
            ...(this.parent?.() || {}),
            sortDirection: {
                default: null,
                parseHTML: (element) => element.getAttribute("data-sort-direction") || null,
                renderHTML: (attributes) => attributes.sortDirection
                    ? { "data-sort-direction": attributes.sortDirection }
                    : {}
            }
        };
    }
});

// Classify a cell's text so numbers and dates sort numerically instead of
// lexically. Shared by the edit-mode (node) and view-mode (DOM) paths so both
// order rows identically.
function classifySortText(rawText) {
    const text = String(rawText || "").replace(/\s+/g, " ").trim();
    if (!text) return { type: "empty", value: "" };

    const isoDate = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(text);
    const dmyDate = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/.exec(text);
    if (isoDate || dmyDate) {
        const [, first, second, third] = isoDate || dmyDate;
        const year = Number(isoDate ? first : third);
        const month = Number(second);
        const day = Number(isoDate ? third : first);
        const timestamp = Date.UTC(year, month - 1, day);
        if (!Number.isNaN(timestamp)) return { type: "date", value: timestamp };
    }

    const numeric = Number(text.replace(/[\s,]/g, ""));
    if (text && Number.isFinite(numeric)) return { type: "number", value: numeric };
    return { type: "text", value: text };
}

function getTableSortValue(cell) {
    return classifySortText(extractNodeText(cell));
}

function getTableSortValueFromDom(rowDom, columnIndex) {
    const cell = rowDom.children[columnIndex];
    if (!cell) return { type: "empty", value: "" };
    return classifySortText(cell.textContent || "");
}

// Empty cells always sink to the bottom regardless of direction; ties keep the
// original order so sorting stays stable.
function compareSortEntries(left, right, direction, collator) {
    if (left.sortValue.type === "empty" || right.sortValue.type === "empty") {
        if (left.sortValue.type === right.sortValue.type) return left.index - right.index;
        return left.sortValue.type === "empty" ? 1 : -1;
    }
    let comparison;
    if (left.sortValue.type === right.sortValue.type && left.sortValue.type !== "text") {
        comparison = left.sortValue.value - right.sortValue.value;
    } else {
        comparison = collator.compare(String(left.sortValue.value), String(right.sortValue.value));
    }
    return comparison === 0 ? left.index - right.index : (direction === "asc" ? comparison : -comparison);
}

// View mode (published / read-only pages): reorder the rendered table DOM in
// place. This deliberately does NOT touch the ProseMirror document — the change
// is temporary, never saved, and resets to the published order on refresh, and
// it can never leak into the editable content. ProseMirror renders tables as
// <table><tbody><tr><th>…</tr>…</tbody></table> (no <thead>), so the header is
// the first <tr> and stays put while only the body rows below it are reordered.
// The direction toggles from whatever the DOM currently shows, so repeated
// clicks alternate asc → desc → asc.
function sortTableDomInView(tableDom, columnIndex, headerRow) {
    if (!tableDom || !headerRow) return false;

    // Only sort rows that are NOT the header row
    const allRows = Array.from(tableDom.querySelectorAll(":scope > tbody > tr, :scope > tr"));
    const bodyTrs = allRows.filter((row) => row !== headerRow);

    if (bodyTrs.length < 1) return false;

    const headerCells = Array.from(headerRow.children);
    if (columnIndex < 0 || columnIndex >= headerCells.length) return false;

    // Reordering a grid with merged cells needs a grid-aware implementation;
    // bail out so the table is never scrambled.
    const hasMergedCells = allRows.some((tr) =>
        tr.children.length !== headerCells.length ||
        Array.from(tr.children).some((cell) =>
            Number(cell.getAttribute("colspan") || 1) !== 1 ||
            Number(cell.getAttribute("rowspan") || 1) !== 1));
    if (hasMergedCells) return false;

    const activeCell = headerCells[columnIndex];
    const currentDirection = activeCell.getAttribute("data-sort-direction");
    const direction = currentDirection === "asc" ? "desc" : "asc";
    headerCells.forEach((cell, index) => {
        if (index === columnIndex) cell.setAttribute("data-sort-direction", direction);
        else cell.removeAttribute("data-sort-direction");
    });

    const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });
    const sortedRows = bodyTrs.map((row, index) => ({ row, index, sortValue: getTableSortValueFromDom(row, columnIndex) }));
    sortedRows.sort((left, right) => compareSortEntries(left, right, direction, collator));

    // Insert sorted rows after the header row in order
    const headerParent = headerRow.parentNode;
    sortedRows.forEach(({ row }) => {
        if (row.parentNode === headerParent) {
            headerParent.insertBefore(row, headerRow.nextSibling);
        }
    });
    return true;
}

// Edit mode: persist the sort (state + order) into the ProseMirror document via
// a transaction, so ProseMirror stays in sync and the order survives reload.
function sortTableRows(view, tablePos, tableNode, columnIndex) {
    if (!tableNode || tableNode.childCount < 2) return false;
    const headerRow = tableNode.child(0);
    const headerCell = headerRow.child(columnIndex);
    if (!headerCell) return false;

    // Sorting rows with merged cells would need a grid-aware implementation.
    // Keep the first version deliberately safe instead of reordering columns.
    const hasMergedCells = Array.from({ length: tableNode.childCount }, (_, rowIndex) => tableNode.child(rowIndex))
        .some((row) => row.childCount !== headerRow.childCount || row.content.content.some((cell) =>
            Number(cell.attrs?.colspan || 1) !== 1 || Number(cell.attrs?.rowspan || 1) !== 1
        ));
    if (hasMergedCells) return false;

    const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

    // Edit mode: persist the sort state and the sorted order into the document
    // so it survives Publish/reload.
    const direction = headerCell.attrs?.sortDirection === "asc" ? "desc" : "asc";
    const bodyRows = [];
    for (let index = 1; index < tableNode.childCount; index += 1) {
        const row = tableNode.child(index);
        bodyRows.push({ row, index, sortValue: getTableSortValue(row.child(columnIndex)) });
    }
    bodyRows.sort((left, right) => compareSortEntries(left, right, direction, collator));

    const nextHeaderCells = [];
    headerRow.forEach((cell, _offset, index) => {
        nextHeaderCells.push(cell.type.create(
            { ...cell.attrs, sortDirection: index === columnIndex ? direction : null },
            cell.content,
            cell.marks
        ));
    });
    const nextHeader = headerRow.type.create(headerRow.attrs, Fragment.fromArray(nextHeaderCells), headerRow.marks);
    const nextTable = tableNode.type.create(
        tableNode.attrs,
        Fragment.fromArray([nextHeader, ...bodyRows.map(({ row }) => row)]),
        tableNode.marks
    );
    view.dispatch(view.state.tr.replaceWith(tablePos, tablePos + tableNode.nodeSize, nextTable));
    return true;
}

// Width (px) of the sort-arrow "toggler" zone at the right edge of a header.
// In edit mode only clicks inside this zone sort, so the rest of the header
// stays clickable for editing its text. It lines up with the arrows the CSS
// draws at `right: 7px`.
const DOCS_SORT_TOGGLER_ZONE_PX = 28;

// Edit mode: map the clicked header DOM back to its ProseMirror table position
// and persist the sort through a transaction (via sortTableRows).
function sortTableInEditFromDom(view, tableDom, header, columnIndex) {
    let pos;
    try {
        pos = view.posAtDOM(header, 0);
    } catch (_) {
        return false;
    }
    if (typeof pos !== "number" || pos < 0) return false;
    const $pos = view.state.doc.resolve(pos);
    for (let depth = $pos.depth; depth > 0; depth -= 1) {
        if ($pos.node(depth)?.type?.name !== "table") continue;
        return sortTableRows(view, $pos.before(depth), $pos.node(depth), columnIndex);
    }
    return false;
}

const DocsTableSorting = Extension.create({
    name: "docsTableSorting",

    addProseMirrorPlugins() {
        // A single native click listener drives both modes. We deliberately do
        // NOT use ProseMirror's handleClick prop: in edit mode the table's own
        // plugins consume the click before our prop runs, and in view mode
        // reordering the DOM desyncs ProseMirror's click routing — either way
        // handleClick fires unreliably. A native DOM listener fires on every
        // real click regardless of ProseMirror's internal handling.
        return [new Plugin({
            view(editorView) {
                const onClick = (event) => {
                    if (!(event.target instanceof Element)) return;
                    const header = event.target.closest("th");
                    if (!header || !editorView.dom.contains(header)) return;
                    const tableDom = header.closest("table");
                    const headerRow = header.parentElement;
                    if (!tableDom || !headerRow) return;
                    const columnIndex = Array.from(headerRow.children).indexOf(header);
                    if (columnIndex < 0) return;

                    if (!editorView.editable) {
                        // View mode (published page): the whole header cell is a
                        // sort control. DOM-only, temporary, resets on refresh.
                        if (sortTableDomInView(tableDom, columnIndex, headerRow)) {
                            event.preventDefault();
                        }
                        return;
                    }

                    // Edit mode: only the arrow toggler zone at the right edge
                    // sorts, so clicking header text still edits it. The sort is
                    // persisted into the document so it becomes the saved order.
                    const rect = header.getBoundingClientRect();
                    if (event.clientX < rect.right - DOCS_SORT_TOGGLER_ZONE_PX) return;
                    if (sortTableInEditFromDom(editorView, tableDom, header, columnIndex)) {
                        event.preventDefault();
                    }
                };
                editorView.dom.addEventListener("click", onClick);
                return {
                    destroy() {
                        editorView.dom.removeEventListener("click", onClick);
                    }
                };
            }
        })];
    }
});

const Callout = Node.create({
    name: "callout",
    group: "block",
    content: "block+",
    defining: true,
    isolating: true,

    addAttributes() {
        return {
            tone: {
                default: "info",
                parseHTML: (element) => element.getAttribute("data-tone") || "info",
                renderHTML: (attributes) => ({
                    "data-tone": CALLOUT_TYPES.has(attributes.tone) ? attributes.tone : "info"
                })
            },
            icon: {
                default: true,
                parseHTML: (element) => element.getAttribute("data-icon") !== "false",
                renderHTML: (attributes) => ({
                    "data-icon": attributes.icon === false ? "false" : "true"
                })
            }
        };
    },

    parseHTML() {
        return [{ tag: "div[data-doc-callout]" }];
    },

    renderHTML({ HTMLAttributes, node }) {
        const tone = CALLOUT_TYPES.has(node.attrs.tone) ? node.attrs.tone : "info";
        const showIcon = node.attrs.icon !== false && tone !== "neutral";
        const children = showIcon
            ? [["span", { class: `docs-callout__icon docs-callout__icon--${tone}` }, getCalloutIconSpec(tone)], ["div", { class: "docs-callout__body" }, 0]]
            : [["div", { class: "docs-callout__body" }, 0]];
        return [
            "div",
            mergeAttributes(HTMLAttributes, {
                "data-doc-callout": "",
                "data-tone": tone,
                class: `docs-callout docs-callout--${tone}`
            }),
            ...children
        ];
    },

    addNodeView() {
        return ({ node, editor, getPos }) => {
            const wrapper = document.createElement("div");
            const body = document.createElement("div");
            body.className = "docs-callout__body";

            let iconEl = null;

            const syncCallout = (currentNode) => {
                const tone = CALLOUT_TYPES.has(currentNode.attrs.tone) ? currentNode.attrs.tone : "info";
                const showIcon = currentNode.attrs.icon !== false && tone !== "neutral";

                wrapper.setAttribute("data-doc-callout", "");
                wrapper.setAttribute("data-tone", tone);
                wrapper.setAttribute("data-icon", currentNode.attrs.icon === false ? "false" : "true");
                wrapper.className = `docs-callout docs-callout--${tone}`;

                if (showIcon) {
                    if (!iconEl) {
                        iconEl = document.createElement("span");
                        iconEl.className = "docs-callout__icon";
                        wrapper.insertBefore(iconEl, body);
                    }
                    iconEl.className = `docs-callout__icon docs-callout__icon--${tone}`;
                    iconEl.replaceChildren();
                    const iconMarkup = getCalloutIconSpec(tone);
                    if (iconMarkup) {
                        const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
                        svg.setAttribute("viewBox", "0 0 24 24");
                        svg.setAttribute("fill", "currentColor");
                        const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
                        path.setAttribute("d", CALLOUT_ICON_PATHS[tone]);
                        svg.appendChild(path);
                        iconEl.appendChild(svg);
                    }
                } else if (iconEl) {
                    iconEl.remove();
                    iconEl = null;
                }
            };

            // Delete button (recycle bin) — hidden in view mode via CSS
            const deleteBtn = document.createElement("button");
            deleteBtn.type = "button";
            deleteBtn.className = "docs-callout__delete";
            deleteBtn.contentEditable = "false";
            deleteBtn.setAttribute("aria-label", "Delete callout");
            deleteBtn.innerHTML = `<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>`;
            deleteBtn.addEventListener("mousedown", (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (typeof getPos !== "function") return;
                const pos = getPos();
                const currentNode = editor.state.doc.nodeAt(pos);
                if (currentNode) {
                    editor.chain().focus().deleteRange({ from: pos, to: pos + currentNode.nodeSize }).run();
                }
            });

            wrapper.appendChild(body);
            wrapper.appendChild(deleteBtn);
            syncCallout(node);

            return {
                dom: wrapper,
                contentDOM: body,
                update: (updatedNode) => {
                    if (updatedNode.type.name !== "callout") return false;
                    syncCallout(updatedNode);
                    return true;
                }
            };
        };
    },

    addCommands() {
        return {
            insertCallout:
                (tone = "info") =>
                ({ commands }) =>
                    commands.insertContent({
                        type: this.name,
                        attrs: {
                            tone: CALLOUT_TYPES.has(tone) ? tone : "info",
                            icon: true
                        },
                        content: [{ type: "paragraph" }]
                    }),
            updateCalloutTone:
                (tone = "info") =>
                ({ state, dispatch }) => {
                    const { $from } = state.selection;
                    for (let d = $from.depth; d > 0; d--) {
                        if ($from.node(d).type.name === "callout") {
                            const pos = $from.before(d);
                            const existing = $from.node(d).attrs;
                            dispatch(state.tr.setNodeMarkup(pos, undefined, {
                                ...existing,
                                tone: CALLOUT_TYPES.has(tone) ? tone : "info"
                            }));
                            return true;
                        }
                    }
                    return false;
                },
            toggleCalloutIcon:
                () =>
                ({ state, dispatch }) => {
                    const { $from } = state.selection;
                    for (let d = $from.depth; d > 0; d--) {
                        if ($from.node(d).type.name === "callout") {
                            const pos = $from.before(d);
                            const existing = $from.node(d).attrs;
                            dispatch(state.tr.setNodeMarkup(pos, undefined, {
                                ...existing,
                                icon: existing.icon === false ? true : false
                            }));
                            return true;
                        }
                    }
                    return false;
                }
        };
    }
});

const StatusChip = Node.create({
    name: "statusChip",
    group: "inline",
    inline: true,
    atom: true,
    selectable: false,

    addAttributes() {
        return {
            label: {
                default: "IN PROGRESS",
                parseHTML: (element) => element.getAttribute("data-label") || element.textContent || "STATUS",
                renderHTML: (attributes) => ({
                    "data-label": String(attributes.label || "STATUS").trim() || "STATUS"
                })
            },
            tone: {
                default: "grey",
                parseHTML: (element) => element.getAttribute("data-tone") || "grey",
                renderHTML: (attributes) => ({
                    "data-tone": STATUS_CHIP_TONES.has(attributes.tone) ? attributes.tone : "grey"
                })
            }
        };
    },

    parseHTML() {
        return [{ tag: "span[data-doc-status-chip]" }];
    },

    renderHTML({ HTMLAttributes, node }) {
        const tone = STATUS_CHIP_TONES.has(node.attrs.tone) ? node.attrs.tone : "grey";
        const label = String(node.attrs.label || "STATUS").trim() || "STATUS";
        return [
            "span",
            mergeAttributes(HTMLAttributes, {
                "data-doc-status-chip": "",
                "data-tone": tone,
                "data-label": label,
                class: `docs-status-chip docs-status-chip--${tone}`
            }),
            label
        ];
    },

    addCommands() {
        return {
            insertStatusChip:
                ({ label = "STATUS", tone = "grey" } = {}) =>
                ({ commands }) =>
                    commands.insertContent([
                        {
                            type: this.name,
                            attrs: {
                                label: String(label || "STATUS").trim() || "STATUS",
                                tone: STATUS_CHIP_TONES.has(tone) ? tone : "grey"
                            }
                        },
                        {
                            type: "text",
                            text: " "
                        }
                    ]),
            updateStatusChip:
                ({ label = "STATUS", tone = "grey" } = {}) =>
                ({ state, dispatch }) => {
                    const activeChip = getActiveStatusChipSelection(state);
                    if (!activeChip) return false;
                    const attrs = {
                        ...activeChip.node.attrs,
                        label: String(label || "STATUS").trim() || "STATUS",
                        tone: STATUS_CHIP_TONES.has(tone) ? tone : "grey"
                    };
                    dispatch(state.tr.setNodeMarkup(activeChip.pos, undefined, attrs));
                    return true;
                }
        };
    }
});

// ── Date Chip ─────────────────────────────────────────────────────────────

function formatDateChipDisplay(isoDate) {
    if (!isoDate) return "Date";
    try {
        const [y, m, d] = isoDate.split("-").map(Number);
        return new Date(y, m - 1, d).toLocaleDateString("en-US", {
            month: "short", day: "numeric", year: "numeric"
        });
    } catch { return isoDate; }
}

function getActiveDateChipSelection(state) {
    const { selection } = state;
    if (selection?.node?.type?.name === "dateChip") {
        return { node: selection.node, pos: selection.from };
    }
    const $from = selection.$from;
    if ($from?.nodeBefore?.type?.name === "dateChip") {
        return { node: $from.nodeBefore, pos: $from.pos - $from.nodeBefore.nodeSize };
    }
    if ($from?.nodeAfter?.type?.name === "dateChip") {
        return { node: $from.nodeAfter, pos: $from.pos };
    }
    return null;
}

export function getDocsEditorDateChipState() {
    if (!docsEditor) return { active: false, date: "" };
    const chip = getActiveDateChipSelection(docsEditor.state);
    if (!chip) return { active: false, date: "" };
    return { active: true, date: chip.node.attrs.date || "" };
}

const DateChip = Node.create({
    name: "dateChip",
    group: "inline",
    inline: true,
    atom: true,
    selectable: false,

    addAttributes() {
        return {
            date: {
                default: "",
                parseHTML: (el) => el.getAttribute("data-date") || "",
                renderHTML: (attrs) => ({ "data-date": attrs.date || "" })
            }
        };
    },

    parseHTML() {
        return [{ tag: "span[data-doc-date-chip]" }];
    },

    renderHTML({ HTMLAttributes, node }) {
        const display = formatDateChipDisplay(node.attrs.date);
        return [
            "span",
            mergeAttributes(HTMLAttributes, {
                "data-doc-date-chip": "",
                "data-date": node.attrs.date,
                class: "docs-date-chip"
            }),
            display
        ];
    },

    addCommands() {
        return {
            insertDateChip:
                ({ date = "" } = {}) =>
                ({ commands }) =>
                    commands.insertContent([
                        { type: this.name, attrs: { date } },
                        { type: "text", text: " " }
                    ]),
            updateDateChip:
                ({ date = "" } = {}) =>
                ({ state, dispatch }) => {
                    const chip = getActiveDateChipSelection(state);
                    if (!chip) return false;
                    dispatch(state.tr.setNodeMarkup(chip.pos, undefined, { date }));
                    return true;
                }
        };
    },

    addProseMirrorPlugins() {
        return [
            new Plugin({
                props: {
                    handleTextInput(view, from, _to, text) {
                        if (text !== " ") return false;
                        const $from = view.state.doc.resolve(from);
                        const textBefore = $from.parent.textBetween(
                            Math.max(0, $from.parentOffset - 5),
                            $from.parentOffset,
                            null,
                            "\ufffc"
                        );
                        if (textBefore !== "/date") return false;
                        // Capture cursor coords BEFORE deleting (coords shift after)
                        const coords = view.coordsAtPos(from);
                        const tr = view.state.tr.delete(from - 5, from);
                        view.dispatch(tr);
                        if (typeof document !== "undefined") {
                            document.dispatchEvent(new CustomEvent("docs-date-chip-trigger", {
                                detail: { x: coords.left, y: coords.bottom }
                            }));
                        }
                        return true;
                    }
                }
            })
        ];
    }
});

const DocColumn = Node.create({
    name: "docColumn",
    group: "docColumnBlock",
    content: "block+",
    isolating: true,

    parseHTML() {
        return [{ tag: "div[data-doc-column]" }];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            "div",
            mergeAttributes(HTMLAttributes, {
                "data-doc-column": "",
                class: "docs-column"
            }),
            0
        ];
    }
});

const DocColumns = Node.create({
    name: "docColumns",
    group: "block",
    content: "docColumn{2}",
    isolating: true,
    defining: true,

    parseHTML() {
        return [{ tag: "div[data-doc-columns]" }];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            "div",
            mergeAttributes(HTMLAttributes, {
                "data-doc-columns": "",
                class: "docs-columns"
            }),
            0
        ];
    },

    addCommands() {
        return {
            insertTwoColumns:
                () =>
                ({ commands }) =>
                    commands.insertContent({
                        type: this.name,
                        content: [
                            {
                                type: "docColumn",
                                content: [
                                    {
                                        type: "paragraph",
                                        content: [{ type: "text", text: "Left column" }]
                                    }
                                ]
                            },
                            {
                                type: "docColumn",
                                content: [
                                    {
                                        type: "paragraph",
                                        content: [{ type: "text", text: "Right column" }]
                                    }
                                ]
                            }
                        ]
                    })
        };
    }
});

const TableOfContentsBlock = Node.create({
    name: "tableOfContentsBlock",
    group: "block",
    atom: true,
    selectable: true,

    parseHTML() {
        return [{ tag: "div[data-doc-toc-block]" }];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            "div",
            mergeAttributes(HTMLAttributes, {
                "data-doc-toc-block": "",
                class: "docs-toc-block"
            })
        ];
    },

    addCommands() {
        return {
            insertTableOfContents:
                () =>
                ({ commands }) =>
                    commands.insertContent({
                        type: this.name
                    })
        };
    }
});

const ChildListBlock = Node.create({
    name: "childListBlock",
    group: "block",
    atom: true,
    selectable: true,

    parseHTML() {
        return [{ tag: "div[data-doc-child-list-block]" }];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            "div",
            mergeAttributes(HTMLAttributes, {
                "data-doc-child-list-block": "",
                class: "docs-child-list-block"
            })
        ];
    },

    addCommands() {
        return {
            insertChildList:
                () =>
                ({ commands }) =>
                    commands.insertContent({
                        type: this.name
                    })
        };
    }
});

// Program Roadmap macro: the Forge `confluence-program-roadmap` app ported verbatim and
// hosted in an iframe (src/docs/program-roadmap/). The node's `board` attr plays the role
// of Forge KVS; a null board is a fresh macro (the app shows its first-run state).
const createProgramRoadmapInstanceId = () => `roadmap-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const ProgramTimelineBlock = Node.create({
    name: "programTimelineBlock",
    group: "block",
    atom: true,
    draggable: true,
    selectable: true,

    addAttributes() {
        return {
            board: {
                default: null,
                parseHTML: () => null,
                renderHTML: () => ({})
            },
            instanceId: {
                default: null,
                parseHTML: () => null,
                renderHTML: () => ({})
            }
        };
    },

    parseHTML() {
        return [{ tag: "div[data-doc-program-timeline]" }];
    },

    renderHTML({ HTMLAttributes }) {
        return ["div", mergeAttributes(HTMLAttributes, {
            "data-doc-program-timeline": "",
            class: "docs-program-roadmap"
        })];
    },

    addCommands() {
        return {
            insertProgramTimeline:
                () =>
                ({ commands }) => commands.insertContent({
                    type: this.name,
                    attrs: { board: null, instanceId: createProgramRoadmapInstanceId() }
                })
        };
    },

    addNodeView() {
        return ({ node, editor, getPos }) => {
            const wrapper = document.createElement("div");
            wrapper.className = "docs-program-roadmap";
            wrapper.contentEditable = "false";
            wrapper.setAttribute("data-doc-program-timeline", "");

            let currentNode = node;
            let lastWrittenBoard = node.attrs.board;
            const instanceId = node.attrs.instanceId || createProgramRoadmapInstanceId();

            const roadmap = mountProgramRoadmap({
                instanceKey: instanceId,
                title: docsT("docs.toolbar.programTimeline", "Program timeline"),
                // Public pages have no authenticated save: the roadmap is read-only there.
                readOnly: !docsEditorHooks.onEmbedSave,
                persist: () => (docsEditorHooks.onEmbedSave ? docsEditorHooks.onEmbedSave() : Promise.resolve(false)),
                getData: () => (docsEditorHooks.getEmbedData ? docsEditorHooks.getEmbedData() : null),
                readBoard: () => currentNode.attrs.board,
                writeBoard: (board) => {
                    const position = typeof getPos === "function" ? getPos() : null;
                    if (typeof position !== "number") throw new Error("The roadmap is no longer in this page.");
                    lastWrittenBoard = board;
                    // Also dispatched in view mode, like Confluence: the editor's onUpdate
                    // hook autosaves read-only changes (see onCheckboxChange). The roadmap
                    // has its own undo stack, so keep its saves out of the document history.
                    const transaction = editor.view.state.tr
                        .setNodeMarkup(position, undefined, { ...currentNode.attrs, instanceId, board })
                        .setMeta("addToHistory", false);
                    editor.view.dispatch(transaction);
                }
            });
            wrapper.appendChild(roadmap.frame);

            return {
                dom: wrapper,
                // The app resizes its iframe; those style changes are not document edits.
                ignoreMutation() {
                    return true;
                },
                update(updatedNode) {
                    if (updatedNode.type.name !== "programTimelineBlock") return false;
                    currentNode = updatedNode;
                    if (updatedNode.attrs.board !== lastWrittenBoard) {
                        lastWrittenBoard = updatedNode.attrs.board;
                        roadmap.refresh();
                    }
                    return true;
                },
                destroy() {
                    roadmap.destroy();
                }
            };
        };
    }
});

const DocsImage = Node.create({
    name: "docsImage",
    group: "block",
    atom: true,
    draggable: true,
    selectable: true,

    addAttributes() {
        return {
            src: {
                default: null
            },
            alt: {
                default: null
            },
            title: {
                default: null
            },
            width: {
                default: null,
                parseHTML: (element) => {
                    const raw = element.getAttribute("data-width") || element.getAttribute("width");
                    const parsed = Number.parseInt(raw || "", 10);
                    return Number.isFinite(parsed) ? parsed : null;
                },
                renderHTML: (attributes) => (
                    Number.isFinite(Number(attributes.width))
                        ? { "data-width": String(Number(attributes.width)) }
                        : {}
                )
            },
            fileKey: {
                default: null,
                parseHTML: (element) => element.getAttribute("data-file-key"),
                renderHTML: (attributes) => (
                    attributes.fileKey ? { "data-file-key": attributes.fileKey } : {}
                )
            }
        };
    },

    parseHTML() {
        return [{ tag: DOCS_IMAGE_TAG }];
    },

    renderHTML({ HTMLAttributes }) {
        const width = Number.parseInt(HTMLAttributes["data-width"] || "", 10);
        return [
            "figure",
            {
                class: "docs-image-wrap",
                style: Number.isFinite(width) ? `width:${width}px;max-width:100%;` : ""
            },
            [
                "img",
                mergeAttributes(HTMLAttributes, {
                    "data-doc-image": "",
                    class: "docs-inline-image",
                    style: Number.isFinite(width) ? `width:${width}px;max-width:100%;` : ""
                })
            ]
        ];
    },

    addNodeView() {
        return ({ node, editor, getPos }) => {
            let currentAttrs = { ...node.attrs };
            const wrapper = document.createElement("figure");
            wrapper.className = "docs-image-wrap";
            wrapper.tabIndex = editor.isEditable ? 0 : -1;

            const image = document.createElement("img");
            image.className = "docs-inline-image";
            image.setAttribute("data-doc-image", "");
            wrapper.appendChild(image);

            const controls = document.createElement("div");
            controls.className = "docs-image-controls";

            const presetButtons = [
                { label: "S", ratio: 0.33, title: docsT("docs.imageSize.small", "Small") },
                { label: "M", ratio: 0.5, title: docsT("docs.imageSize.medium", "Medium") },
                { label: "L", ratio: 0.66, title: docsT("docs.imageSize.large", "Large") },
                { label: "Full", labelShort: "F", ratio: 1, title: docsT("docs.imageSize.full", "Full width") }
            ].map((preset) => {
                const button = document.createElement("button");
                button.type = "button";
                button.className = "docs-image-preset-btn";
                button.title = preset.title;
                button.setAttribute("aria-label", preset.title);
                button.dataset.ratio = String(preset.ratio);
                button.dataset.label = preset.label;
                if (preset.labelShort) button.dataset.labelShort = preset.labelShort;
                controls.appendChild(button);
                return button;
            });
            wrapper.appendChild(controls);

            const sizeBadge = document.createElement("div");
            sizeBadge.className = "docs-image-size-badge";
            wrapper.appendChild(sizeBadge);

            const handle = document.createElement("button");
            handle.type = "button";
            handle.className = "docs-image-resize-handle";
            handle.setAttribute("aria-label", docsT("docs.imageResize", "Resize image"));
            wrapper.appendChild(handle);

            const getAvailableWidth = () => {
                // Hard ceiling: the docs canvas body. Its width is set by the page layout
                // and does NOT grow when a table expands — unlike cell.clientWidth which
                // reflects any auto-layout expansion caused by an oversized image.
                // Using this as the upper bound breaks the feedback loop where a growing
                // image forces the column wider, which raises the clamp, which allows
                // the image to grow further.
                const canvasBody = wrapper.closest(".docs-canvas__body");
                const pageCeiling = canvasBody ? Math.round(canvasBody.clientWidth) : 0;

                const cellWidth = wrapper.closest("td, th")?.clientWidth || 0;
                if (cellWidth > 0) {
                    // Normal case: use the actual cell width, but cap at the page ceiling
                    // so an already-inflated cell cannot raise the constraint beyond the page.
                    const effective = pageCeiling > 0 ? Math.min(cellWidth, pageCeiling) : cellWidth;
                    return Math.max(DOCS_IMAGE_MIN_WIDTH, Math.round(effective));
                }
                const canvasBodyWidth = canvasBody?.clientWidth || 0;
                const editorContentWidth = editor.view?.dom?.clientWidth || 0;
                const parentWidth = wrapper.parentElement?.clientWidth || 0;
                const availableWidth = canvasBodyWidth || editorContentWidth || parentWidth || DOCS_IMAGE_DEFAULT_WIDTH;
                return Math.max(DOCS_IMAGE_MIN_WIDTH, Math.round(availableWidth));
            };

            const setDraftWidth = (nextWidth) => {
                const clampedWidth = Math.max(DOCS_IMAGE_MIN_WIDTH, Math.min(getAvailableWidth(), Math.round(nextWidth)));
                wrapper.style.width = `${clampedWidth}px`;
                image.style.width = `${clampedWidth}px`;
                sizeBadge.dataset.width = `${clampedWidth}px`;
                const availableWidth = getAvailableWidth();
                presetButtons.forEach((button) => {
                    const ratio = Number.parseFloat(button.dataset.ratio || "0");
                    const targetWidth = Math.round(availableWidth * ratio);
                    button.classList.toggle("active", Math.abs(targetWidth - clampedWidth) <= 24);
                });
                return clampedWidth;
            };

            const applySavedWidth = (nextWidth) => {
                // Clamp to the available width so a stored value wider than the current
                // page/column never inflates the table on load or on node update.
                // getAvailableWidth() now caps at canvasBody width, which is always a
                // valid stable value even before the cell is fully laid out.
                const width = Math.max(DOCS_IMAGE_MIN_WIDTH, Math.min(getAvailableWidth(), Math.round(nextWidth)));
                wrapper.style.width = `${width}px`;
                image.style.width = `${width}px`;
                sizeBadge.dataset.width = `${width}px`;
                const availableWidth = getAvailableWidth();
                presetButtons.forEach((button) => {
                    const ratio = Number.parseFloat(button.dataset.ratio || "0");
                    const targetWidth = Math.round(availableWidth * ratio);
                    button.classList.toggle("active", Math.abs(targetWidth - width) <= 24);
                });
                return width;
            };

            const commitWidth = (nextWidth) => {
                if (typeof getPos !== "function") return;
                const width = setDraftWidth(nextWidth);
                const pos = getPos();
                editor.commands.command(({ tr }) => {
                    tr.setNodeMarkup(pos, undefined, {
                        ...currentAttrs,
                        width
                    });
                    return true;
                });
            };

            const setErrorState = (hasError) => {
                wrapper.classList.toggle("docs-image-error", hasError);
                if (hasError) {
                    controls.style.display = "none";
                    sizeBadge.style.display = "none";
                    handle.style.display = "none";
                } else if (editor.isEditable) {
                    controls.style.removeProperty("display");
                    sizeBadge.style.removeProperty("display");
                    handle.style.removeProperty("display");
                }
            };

            image.addEventListener("error", () => setErrorState(true));
            image.addEventListener("load", () => setErrorState(false));

            const applyAttrs = (attrs) => {
                // Reset error state before loading new src
                setErrorState(false);
                image.src = attrs.src || "";
                image.alt = attrs.alt || "";
                image.title = attrs.title || "";
                if (attrs.fileKey) {
                    image.setAttribute("data-file-key", attrs.fileKey);
                } else {
                    image.removeAttribute("data-file-key");
                }

                const width = Number.parseInt(attrs.width || "", 10);
                if (Number.isFinite(width)) {
                    applySavedWidth(width);
                } else {
                    wrapper.style.removeProperty("width");
                    image.style.removeProperty("width");
                    const fallbackWidth = Math.min(getAvailableWidth(), wrapper.getBoundingClientRect().width || DOCS_IMAGE_DEFAULT_WIDTH);
                    sizeBadge.dataset.width = `${Math.round(fallbackWidth)}px`;
                    presetButtons.forEach((button) => button.classList.remove("active"));
                }
            };

            applyAttrs(node.attrs);

            const updateHandleVisibility = () => {
                wrapper.tabIndex = editor.isEditable ? 0 : -1;
                if (editor.isEditable) {
                    controls.style.removeProperty("display");
                    sizeBadge.style.removeProperty("display");
                    handle.style.removeProperty("display");
                } else {
                    controls.style.display = "none";
                    sizeBadge.style.display = "none";
                    handle.style.display = "none";
                }
            };
            updateHandleVisibility();

            presetButtons.forEach((button) => {
                button.addEventListener("mousedown", (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                });
                button.addEventListener("click", (event) => {
                    if (!editor.isEditable) return;
                    event.preventDefault();
                    event.stopPropagation();
                    wrapper.classList.add("is-selected");
                    commitWidth(getAvailableWidth() * Number.parseFloat(button.dataset.ratio || "1"));
                });
            });

            let pointerId = null;
            let startX = 0;
            let startY = 0;
            let startWidth = 0;
            let maxDragWidth = Infinity; // frozen at pointerdown — see comment below

            handle.addEventListener("pointerdown", (event) => {
                if (!editor.isEditable || typeof getPos !== "function") return;
                event.preventDefault();
                event.stopPropagation();

                pointerId = event.pointerId;
                startX = event.clientX;
                startY = event.clientY;

                // Freeze the ceiling NOW, before any drag movement.
                // getAvailableWidth() is now capped at canvasBody width so it returns
                // a reliable value even if the cell has already auto-expanded.
                maxDragWidth = getAvailableWidth();

                // Cap startWidth at the ceiling so there is no dead-zone when the image
                // is already at (or over) the limit — the drag responds immediately.
                const rawStart = wrapper.getBoundingClientRect().width || node.attrs.width || DOCS_IMAGE_DEFAULT_WIDTH;
                startWidth = Math.min(maxDragWidth, Math.round(rawStart));

                handle.setPointerCapture(pointerId);
                wrapper.classList.add("is-resizing");
            });

            handle.addEventListener("pointermove", (event) => {
                if (pointerId == null || event.pointerId !== pointerId) return;
                event.preventDefault();

                const deltaX = event.clientX - startX;
                const deltaY = event.clientY - startY;
                const diagonalDelta = Math.abs(deltaX) >= Math.abs(deltaY) ? deltaX : deltaY;
                const nextWidth = Math.max(
                    DOCS_IMAGE_MIN_WIDTH,
                    Math.min(maxDragWidth, Math.round(startWidth + diagonalDelta))
                );

                setDraftWidth(nextWidth);
            });

            const commitResize = (event) => {
                if (pointerId == null || event.pointerId !== pointerId || typeof getPos !== "function") return;
                event.preventDefault();

                const measuredWidth = Math.round(wrapper.getBoundingClientRect().width);
                commitWidth(measuredWidth);

                try {
                    handle.releasePointerCapture(pointerId);
                } catch {}
                pointerId = null;
                wrapper.classList.remove("is-resizing");
            };

            handle.addEventListener("pointerup", commitResize);
            handle.addEventListener("pointercancel", commitResize);

            const compactObserver = new ResizeObserver(([entry]) => {
                const w = entry.contentRect.width;
                wrapper.classList.toggle("docs-image-wrap--compact", w < 220 && w >= 130);
                wrapper.classList.toggle("docs-image-wrap--mini", w < 130);
            });
            compactObserver.observe(wrapper);

            return {
                dom: wrapper,
                selectNode: () => {
                    wrapper.classList.add("is-selected");
                },
                deselectNode: () => {
                    wrapper.classList.remove("is-selected");
                },
                update: (updatedNode) => {
                    if (updatedNode.type.name !== this.name) return false;
                    currentAttrs = { ...updatedNode.attrs };
                    applyAttrs(updatedNode.attrs);
                    updateHandleVisibility();
                    return true;
                },
                destroy: () => {
                    compactObserver.disconnect();
                }
            };
        };
    },

    addCommands() {
        return {
            setDocsImage:
                (attributes = {}) =>
                ({ commands }) =>
                    commands.insertContent({
                        type: this.name,
                        attrs: attributes
                    })
        };
    }
});

function buildDocsFileNodeAttributes() {
    return {
        fileKey: {
            default: "",
            parseHTML: (element) => element.getAttribute("data-file-key") || "",
            renderHTML: (attributes) => (
                attributes.fileKey ? { "data-file-key": attributes.fileKey } : {}
            )
        },
        name: {
            default: "",
            parseHTML: (element) => element.getAttribute("data-file-name") || "",
            renderHTML: (attributes) => (
                attributes.name ? { "data-file-name": attributes.name } : {}
            )
        },
        mimeType: {
            default: "",
            parseHTML: (element) => element.getAttribute("data-mime-type") || "",
            renderHTML: (attributes) => (
                attributes.mimeType ? { "data-mime-type": attributes.mimeType } : {}
            )
        },
        size: {
            default: 0,
            parseHTML: (element) => Number.parseInt(element.getAttribute("data-file-size") || "0", 10) || 0,
            renderHTML: (attributes) => (
                Number.isFinite(Number(attributes.size)) && Number(attributes.size) > 0
                    ? { "data-file-size": String(Math.round(Number(attributes.size))) }
                    : {}
            )
        },
        fileClass: {
            default: "file",
            parseHTML: (element) => element.getAttribute("data-file-class") || "file",
            renderHTML: (attributes) => ({
                "data-file-class": String(attributes.fileClass || "file")
            })
        },
        extension: {
            default: "",
            parseHTML: (element) => element.getAttribute("data-file-extension") || "",
            renderHTML: (attributes) => (
                attributes.extension ? { "data-file-extension": attributes.extension } : {}
            )
        }
    };
}

function buildDocsFileMetaLabel(presentation) {
    return [presentation.typeLabel, presentation.sizeLabel].filter(Boolean).join(" · ");
}

function applyDocsFileDataAttributes(element, attrs = {}, type = "chip") {
    if (!element) return;
    const normalized = normalizeDocsFileAttrs(attrs);
    element.setAttribute(type === "card" ? "data-doc-file-card" : "data-doc-file-chip", "");
    element.setAttribute("data-file-class", normalized.fileClass || "file");

    if (normalized.fileKey) element.setAttribute("data-file-key", normalized.fileKey);
    else element.removeAttribute("data-file-key");

    if (normalized.name) element.setAttribute("data-file-name", normalized.name);
    else element.removeAttribute("data-file-name");

    if (normalized.mimeType) element.setAttribute("data-mime-type", normalized.mimeType);
    else element.removeAttribute("data-mime-type");

    if (normalized.size > 0) element.setAttribute("data-file-size", String(normalized.size));
    else element.removeAttribute("data-file-size");

    if (normalized.extension) element.setAttribute("data-file-extension", normalized.extension);
    else element.removeAttribute("data-file-extension");
}

function positionDocsFileMenu(wrapper, menu) {
    if (!wrapper || !menu || typeof window === "undefined") return;

    const viewportMargin = 16;
    menu.classList.remove("docs-file-menu--above");
    menu.style.removeProperty("max-height");
    menu.style.removeProperty("overflow-y");

    const wrapperRect = wrapper.getBoundingClientRect();
    const availableBelow = Math.max(0, Math.floor(window.innerHeight - wrapperRect.bottom - viewportMargin));
    const availableAbove = Math.max(0, Math.floor(wrapperRect.top - viewportMargin));
    const menuHeight = Math.ceil(menu.scrollHeight);
    const shouldOpenAbove = menuHeight > availableBelow && availableAbove > availableBelow;
    const availableSpace = shouldOpenAbove ? availableAbove : availableBelow;

    menu.classList.toggle("docs-file-menu--above", shouldOpenAbove);
    menu.style.maxHeight = `${Math.max(72, availableSpace)}px`;
    menu.style.overflowY = menuHeight > availableSpace ? "auto" : "visible";
}

function bindDocsControlButton(button, handler) {
    if (!button) return;
    button.contentEditable = "false";
    button.setAttribute("spellcheck", "false");
    button.addEventListener("mousedown", (event) => {
        event.preventDefault();
        event.stopPropagation();
    });
    button.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        handler();
    });
}

function createDocsFileChipNodeView() {
    return ({ node, editor, getPos }) => {
        let currentAttrs = { ...node.attrs };
        const wrapper = document.createElement("span");
        wrapper.className = "docs-file-chip";
        wrapper.setAttribute("data-doc-file-chip", "");
        wrapper.setAttribute("contenteditable", "false");

        const mainButton = document.createElement("button");
        mainButton.type = "button";
        mainButton.className = "docs-file-chip__main";

        const badge = document.createElement("span");
        badge.className = "docs-file-chip__badge";
        const labelWrap = document.createElement("span");
        labelWrap.className = "docs-file-chip__copy";
        const label = document.createElement("span");
        label.className = "docs-file-chip__label";
        const meta = document.createElement("span");
        meta.className = "docs-file-chip__meta";
        labelWrap.append(label, meta);
        mainButton.append(badge, labelWrap);

        const toggle = document.createElement("button");
        toggle.type = "button";
        toggle.className = "docs-file-chip__toggle docs-file-action--edit-only";
        toggle.setAttribute("aria-label", docsT("docs.fileActions", "File actions"));
        toggle.innerHTML = `<svg viewBox="0 0 16 16" width="14" height="14" fill="currentColor" aria-hidden="true"><circle cx="3" cy="8" r="1.3"></circle><circle cx="8" cy="8" r="1.3"></circle><circle cx="13" cy="8" r="1.3"></circle></svg>`;

        const menu = document.createElement("div");
        menu.className = "docs-file-menu";
        menu.hidden = true;

        const closeMenu = () => {
            menu.hidden = true;
            toggle.setAttribute("aria-expanded", "false");
        };

        const syncMenuPosition = () => {
            if (!menu.hidden) {
                positionDocsFileMenu(wrapper, menu);
            }
        };

        const syncMenu = () => {
            const presentation = getDocsFilePresentation(currentAttrs);
            menu.replaceChildren();

            const makeMenuButton = (text, action, className = "") => {
                const button = document.createElement("button");
                button.type = "button";
                button.className = `docs-file-menu__button ${className}`.trim();
                button.textContent = text;
                bindDocsControlButton(button, () => {
                    closeMenu();
                    action();
                });
                menu.appendChild(button);
            };

            makeMenuButton(
                presentation.previewable ? docsT("docs.fileOpen", "Open") : docsT("docs.fileDownload", "Download"),
                () => openDocsFilePresentation(presentation, !presentation.previewable)
            );

            if (presentation.downloadUrl && presentation.previewable) {
                makeMenuButton(
                    docsT("docs.fileDownload", "Download"),
                    () => openDocsFilePresentation(presentation, true)
                );
            }

            makeMenuButton(
                docsT("docs.fileConvertToCard", "Convert to card"),
                () => {
                    if (typeof getPos !== "function") return;
                    const pos = getPos();
                    const currentNode = editor.state.doc.nodeAt(pos);
                    if (!currentNode) return;
                    replaceDocsFileChipWithCard(editor, pos, currentNode);
                },
                "docs-file-action--edit-only"
            );

            makeMenuButton(
                docsT("docs.fileRemove", "Remove"),
                () => {
                    if (typeof getPos !== "function") return;
                    const pos = getPos();
                    const currentNode = editor.state.doc.nodeAt(pos);
                    if (!currentNode) return;
                    editor.chain().focus().deleteRange({ from: pos, to: pos + currentNode.nodeSize }).run();
                },
                "docs-file-menu__button--danger docs-file-action--edit-only"
            );
        };

        bindDocsControlButton(mainButton, () => {
            openDocsFilePresentation(getDocsFilePresentation(currentAttrs), false);
        });

        bindDocsControlButton(toggle, () => {
            if (menu.hidden) {
                syncMenu();
                menu.hidden = false;
                toggle.setAttribute("aria-expanded", "true");
                syncMenuPosition();
                return;
            }
            closeMenu();
        });
        toggle.setAttribute("aria-expanded", "false");

        const onDocumentClick = (event) => {
            if (!wrapper.contains(event.target)) {
                closeMenu();
            }
        };
        document.addEventListener("click", onDocumentClick, true);
        window.addEventListener("resize", syncMenuPosition);
        document.addEventListener("scroll", syncMenuPosition, true);

        const applyPresentation = (attrs) => {
            currentAttrs = { ...attrs };
            const presentation = getDocsFilePresentation(attrs);
            const metaLabel = buildDocsFileMetaLabel(presentation);

            wrapper.className = `docs-file-chip docs-file-chip--${presentation.fileClass}`;
            applyDocsFileDataAttributes(wrapper, attrs, "chip");
            badge.textContent = presentation.badgeLabel;
            label.textContent = presentation.name;
            meta.textContent = metaLabel;
            meta.hidden = !metaLabel;
            mainButton.setAttribute("aria-label", `${presentation.primaryActionLabel}: ${presentation.name}`);
        };

        wrapper.addEventListener("click", (event) => {
            if (event.target === wrapper && typeof getPos === "function") {
                event.preventDefault();
                event.stopPropagation();
                const pos = getPos();
                if (pos != null) {
                    const tr = editor.state.tr.setSelection(NodeSelection.create(editor.state.doc, pos));
                    editor.view.dispatch(tr);
                }
            }
        });

        wrapper.append(mainButton, toggle, menu);
        applyPresentation(node.attrs);

        return {
            dom: wrapper,
            selectNode: () => wrapper.classList.add("is-selected"),
            deselectNode: () => wrapper.classList.remove("is-selected"),
            update: (updatedNode) => {
                if (updatedNode.type.name !== "docsFileChip") return false;
                applyPresentation(updatedNode.attrs);
                syncMenuPosition();
                return true;
            },
            destroy: () => {
                document.removeEventListener("click", onDocumentClick, true);
                window.removeEventListener("resize", syncMenuPosition);
                document.removeEventListener("scroll", syncMenuPosition, true);
            }
        };
    };
}

function createDocsFileCardNodeView() {
    return ({ node, editor, getPos }) => {
        let currentAttrs = { ...node.attrs };
        const wrapper = document.createElement("div");
        wrapper.className = "docs-file-card";
        wrapper.setAttribute("data-doc-file-card", "");
        wrapper.setAttribute("contenteditable", "false");

        const badge = document.createElement("div");
        badge.className = "docs-file-card__badge";

        const copy = document.createElement("div");
        copy.className = "docs-file-card__copy";
        const eyebrow = document.createElement("div");
        eyebrow.className = "docs-file-card__eyebrow";
        const title = document.createElement("div");
        title.className = "docs-file-card__title";
        const meta = document.createElement("div");
        meta.className = "docs-file-card__meta";
        copy.append(eyebrow, title, meta);

        const primaryButton = document.createElement("button");
        primaryButton.type = "button";
        primaryButton.className = "docs-file-card__action docs-file-card__action--primary";
        const downloadButton = document.createElement("button");
        downloadButton.type = "button";
        downloadButton.className = "docs-file-card__action";
        const convertButton = document.createElement("button");
        convertButton.type = "button";
        convertButton.className = "docs-file-card__action docs-file-action--edit-only";
        const removeButton = document.createElement("button");
        removeButton.type = "button";
        removeButton.className = "docs-file-card__action docs-file-card__action--danger docs-file-action--edit-only";

        const actions = document.createElement("div");
        actions.className = "docs-file-card__actions";
        actions.append(primaryButton, downloadButton, convertButton, removeButton);

        const shell = document.createElement("div");
        shell.className = "docs-file-card__shell";
        shell.append(badge, copy, actions);
        wrapper.appendChild(shell);

        bindDocsControlButton(primaryButton, () => {
            openDocsFilePresentation(getDocsFilePresentation(currentAttrs), false);
        });
        bindDocsControlButton(downloadButton, () => {
            openDocsFilePresentation(getDocsFilePresentation(currentAttrs), true);
        });
        bindDocsControlButton(convertButton, () => {
            if (typeof getPos !== "function") return;
            const pos = getPos();
            const currentNode = editor.state.doc.nodeAt(pos);
            if (!currentNode) return;
            replaceDocsFileCardWithChip(editor, pos, currentNode);
        });
        bindDocsControlButton(removeButton, () => {
            if (typeof getPos !== "function") return;
            const pos = getPos();
            const currentNode = editor.state.doc.nodeAt(pos);
            if (!currentNode) return;
            editor.chain().focus().deleteRange({ from: pos, to: pos + currentNode.nodeSize }).run();
        });

        const applyPresentation = (attrs) => {
            currentAttrs = { ...attrs };
            const presentation = getDocsFilePresentation(attrs);
            const metaLabel = buildDocsFileMetaLabel(presentation);
            wrapper.className = `docs-file-card docs-file-card--${presentation.fileClass}`;
            applyDocsFileDataAttributes(wrapper, attrs, "card");
            badge.textContent = presentation.badgeLabel;
            eyebrow.textContent = presentation.typeLabel;
            title.textContent = presentation.name;
            meta.textContent = metaLabel;
            primaryButton.textContent = presentation.previewable ? docsT("docs.fileOpen", "Open") : docsT("docs.fileDownload", "Download");
            downloadButton.textContent = docsT("docs.fileDownload", "Download");
            convertButton.textContent = docsT("docs.fileConvertToChip", "Convert to chip");
            removeButton.textContent = docsT("docs.fileRemove", "Remove");
            downloadButton.hidden = !presentation.downloadUrl || (!presentation.previewable && presentation.downloadUrl === presentation.openUrl);
        };

        applyPresentation(node.attrs);

        return {
            dom: wrapper,
            selectNode: () => wrapper.classList.add("is-selected"),
            deselectNode: () => wrapper.classList.remove("is-selected"),
            update: (updatedNode) => {
                if (updatedNode.type.name !== "docsFileCard") return false;
                applyPresentation(updatedNode.attrs);
                return true;
            }
        };
    };
}

const DocsFileChip = Node.create({
    name: "docsFileChip",
    group: "inline",
    inline: true,
    atom: true,
    draggable: true,
    selectable: true,

    addAttributes() {
        return buildDocsFileNodeAttributes();
    },

    parseHTML() {
        return [
            { tag: "div[data-doc-file-chip]" },
            { tag: "span[data-doc-file-chip]" },
            { tag: "a[data-doc-file-chip]" }
        ];
    },

    renderHTML({ HTMLAttributes, node }) {
        const presentation = getDocsFilePresentation(node.attrs);
        const metaLabel = buildDocsFileMetaLabel(presentation);
        return [
            "span",
            mergeAttributes(HTMLAttributes, {
                "data-doc-file-chip": "",
                class: `docs-file-chip docs-file-chip--${presentation.fileClass}`,
                contenteditable: "false"
            }),
            ["span", { class: "docs-file-chip__badge" }, presentation.badgeLabel],
            ["span", { class: "docs-file-chip__copy" },
                ["span", { class: "docs-file-chip__label" }, presentation.name],
                ["span", { class: "docs-file-chip__meta" }, metaLabel]
            ]
        ];
    },

    addNodeView() {
        return createDocsFileChipNodeView();
    }
});

const DocsFileCard = Node.create({
    name: "docsFileCard",
    group: "block",
    atom: true,
    draggable: true,
    selectable: true,

    addAttributes() {
        return buildDocsFileNodeAttributes();
    },

    parseHTML() {
        return [{ tag: "div[data-doc-file-card]" }];
    },

    renderHTML({ HTMLAttributes, node }) {
        const presentation = getDocsFilePresentation(node.attrs);
        const metaLabel = buildDocsFileMetaLabel(presentation);
        return [
            "div",
            mergeAttributes(HTMLAttributes, {
                "data-doc-file-card": "",
                class: `docs-file-card docs-file-card--${presentation.fileClass}`,
                contenteditable: "false"
            }),
            ["div", { class: "docs-file-card__shell" },
                ["div", { class: "docs-file-card__badge" }, presentation.badgeLabel],
                ["div", { class: "docs-file-card__copy" },
                    ["div", { class: "docs-file-card__eyebrow" }, presentation.typeLabel],
                    ["div", { class: "docs-file-card__title" }, presentation.name],
                    ["div", { class: "docs-file-card__meta" }, metaLabel]
                ]
            ]
        ];
    },

    addNodeView() {
        return createDocsFileCardNodeView();
    }
});

const DocsAttachmentSupport = Extension.create({
    name: "docsAttachmentSupport",

    addCommands() {
        return {
            insertDocsFileAttachment:
                (attributes = {}, options = {}) =>
                ({ editor, commands }) => {
                    const normalized = normalizeDocsFileAttrs(attributes);
                    if (!normalized.fileKey) return false;
                    const requestedDisplay = String(options?.display || "auto");
                    const nodeType = requestedDisplay === "card"
                        ? "docsFileCard"
                        : "docsFileChip";
                    return commands.insertContent({
                        type: nodeType,
                        attrs: normalized
                    });
                },
            convertDocsFileDisplay:
                (display = "card") =>
                ({ editor }) =>
                    convertDocsFileSelection(editor, display)
        };
    },

    addProseMirrorPlugins() {
        const isEditable = () => this.editor?.isEditable === true;
        return [
            new Plugin({
                props: {
                    handlePaste(view, event) {
                        if (!isEditable() || typeof docsFileUploadHandler !== "function") return false;
                        const files = Array.from(event.clipboardData?.files || []).filter(Boolean);
                        if (!files.length) return false;
                        event.preventDefault();
                        void Promise.resolve(docsFileUploadHandler(files, { source: "paste" })).catch((error) => {
                            console.error("Failed to handle docs paste upload", error);
                        });
                        return true;
                    },
                    handleDrop(view, event) {
                        if (!isEditable() || typeof docsFileUploadHandler !== "function") return false;
                        const files = Array.from(event.dataTransfer?.files || []).filter(Boolean);
                        if (!files.length) return false;
                        const dropPos = view.posAtCoords({ left: event.clientX, top: event.clientY });
                        if (dropPos?.pos != null) {
                            const tr = view.state.tr.setSelection(TextSelection.create(view.state.doc, dropPos.pos));
                            view.dispatch(tr);
                            view.focus();
                        }
                        event.preventDefault();
                        void Promise.resolve(docsFileUploadHandler(files, { source: "drop" })).catch((error) => {
                            console.error("Failed to handle docs drop upload", error);
                        });
                        return true;
                    },
                    handleDOMEvents: {
                        dragover(view, event) {
                            if (!isEditable()) return false;
                            const hasFiles = Array.from(event.dataTransfer?.types || []).includes("Files");
                            if (!hasFiles) return false;
                            event.preventDefault();
                            return true;
                        }
                    }
                }
            })
        ];
    }
});

const DocsYoutubeVideo = Node.create({
    name: "docsYoutubeVideo",
    group: "block",
    atom: true,
    draggable: true,
    selectable: true,

    addAttributes() {
        return {
            src: { default: null },
            videoId: { default: null },
            originalUrl: { default: null }
        };
    },

    parseHTML() {
        return [{ tag: "div[data-doc-youtube]" }];
    },

    renderHTML({ HTMLAttributes }) {
        const src = HTMLAttributes.src || "";
        const videoId = HTMLAttributes.videoId || "";
        return [
            "div",
            {
                "data-doc-youtube": "",
                "data-video-id": videoId,
                class: "docs-video-wrap"
            },
            [
                "div",
                { class: "docs-video-frame" },
                [
                    "iframe",
                    {
                        src,
                        title: "YouTube video",
                        allow: "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share",
                        allowfullscreen: "true",
                        loading: "lazy",
                        referrerpolicy: "strict-origin-when-cross-origin",
                        class: "docs-video-iframe"
                    }
                ]
            ]
        ];
    },

    addCommands() {
        return {
            setDocsYoutubeVideo:
                (value) =>
                ({ commands }) => {
                    const normalized = typeof value === "string" ? normalizeYoutubeEmbed(value) : value;
                    if (!normalized?.src || !normalized?.videoId) return false;
                    return commands.insertContent({
                        type: this.name,
                        attrs: {
                            src: normalized.src,
                            videoId: normalized.videoId,
                            originalUrl: typeof value === "string" ? value : (value.originalUrl || normalized.src)
                        }
                    });
                }
        };
    },

    addNodeView() {
        return ({ node }) => {
            // Build DOM once — never recreated unless the video itself changes.
            // This prevents iframe reload/flicker when nearby content updates
            // (e.g. checkbox state changes dispatch a ProseMirror transaction).
            const wrap = document.createElement("div");
            wrap.setAttribute("data-doc-youtube", "");
            wrap.setAttribute("data-video-id", node.attrs.videoId || "");
            wrap.className = "docs-video-wrap";

            const frame = document.createElement("div");
            frame.className = "docs-video-frame";

            const iframe = document.createElement("iframe");
            iframe.src = node.attrs.src || "";
            iframe.title = "YouTube video";
            iframe.setAttribute("allow", "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share");
            iframe.setAttribute("allowfullscreen", "true");
            iframe.setAttribute("loading", "lazy");
            iframe.setAttribute("referrerpolicy", "strict-origin-when-cross-origin");
            iframe.className = "docs-video-iframe";

            frame.appendChild(iframe);
            wrap.appendChild(frame);

            return {
                dom: wrap,
                update(updatedNode) {
                    // Return true (keep existing DOM) when it's still the same video.
                    // Returning false would cause ProseMirror to destroy and recreate
                    // the DOM, reloading the iframe and causing visible flicker.
                    if (updatedNode.type.name !== "docsYoutubeVideo") return false;
                    if (updatedNode.attrs.videoId !== node.attrs.videoId) return false;
                    return true;
                }
            };
        };
    }
});

let docsEditor = null;
let docsEditorElement = null;
let docsEditorKeydownHandler = null;
let docsLayoutSyncRafA = 0;
let docsLayoutSyncRafB = 0;
let _docsChildPages = [];
let docsLayoutResizeObserver = null;
let docsTableResizeReleaseHandler = null;
let docsColumnResizeInProgress = false; // true only during an actual col-resize drag
let docsEditorHooks = {
    onCreate: null,
    onUpdate: null,
    onFocus: null,
    onBlur: null,
    onCheckboxChange: null,
    // Saves the page now and resolves to whether the server confirmed it. Absent on
    // read-only surfaces (public pages), where embeds must not be editable.
    onEmbedSave: null,
    // Returns task/project data plus openTask(id) and updateTaskDates(id, start, end) for linked embeds.
    getEmbedData: null
};

function normalizeDocsFileChipPlacement(node) {
    if (!node || typeof node !== "object") return node;
    if (!Array.isArray(node.content)) return node;

    const newContent = [];
    for (const child of node.content) {
        // docsFileCard is a block node. If it was saved inside a paragraph (from a brief
        // period when it was inline), extract it back to block level.
        if (child.type === "paragraph" && Array.isArray(child.content)) {
            const hasBlockCard = child.content.some((c) => c.type === "docsFileCard");
            if (hasBlockCard) {
                let segment = [];
                for (const inline of child.content) {
                    if (inline.type === "docsFileCard") {
                        if (segment.length > 0) {
                            newContent.push({ ...child, content: segment });
                            segment = [];
                        }
                        newContent.push(inline);
                    } else {
                        segment.push(inline);
                    }
                }
                if (segment.length > 0) {
                    newContent.push({ ...child, content: segment });
                }
                continue;
            }
        }
        // docsFileChip is inline — it is valid inside a paragraph; leave it in place.
        newContent.push(normalizeDocsFileChipPlacement(child));
    }
    return { ...node, content: newContent };
}

function normalizeDocJson(value) {
    if (!value || typeof value !== "object") return EMPTY_DOC;
    if (value.type !== "doc") return EMPTY_DOC;
    return normalizeDocsFileChipPlacement(value);
}

// ── Code-block popup menu (singleton, body-level, position:fixed) ───────────
// Opened when the user clicks a code block in edit mode.
// Same pattern as openDocsStatusPopover / docs-table-menu.
let _codeMenuAnchor = null; // the wrapper <div> the popup is bound to

function _getOrCreateCodeMenu() {
    let menu = document.getElementById("docs-code-block-menu");
    if (!menu) {
        menu = document.createElement("div");
        menu.id = "docs-code-block-menu";
        menu.className = "docs-code-block-menu";
        menu.style.display = "none";
        document.body.appendChild(menu);

        // Close on outside click
        document.addEventListener("mousedown", (e) => {
            if (menu.style.display !== "none" && !menu.contains(e.target) && e.target !== _codeMenuAnchor && !_codeMenuAnchor?.contains(e.target)) {
                _closeCodeBlockMenu();
            }
        }, true);

        // Close on Escape
        document.addEventListener("keydown", (e) => {
            if (e.key === "Escape" && menu.style.display !== "none") {
                _closeCodeBlockMenu();
            }
        }, true);
    }
    return menu;
}

function _closeCodeBlockMenu() {
    const menu = document.getElementById("docs-code-block-menu");
    if (menu) menu.style.display = "none";
    _codeMenuAnchor = null;
}

function _openCodeBlockMenu({ wrapper, wrapped, iconWrap, iconOneLine, iconDelete, onWrap, onDelete }) {
    const menu = _getOrCreateCodeMenu();

    // Rebuild content each time (cheap, keeps state fresh)
    menu.innerHTML = "";

    const wrapBtn = document.createElement("button");
    wrapBtn.type = "button";
    wrapBtn.className = "docs-code-menu-btn docs-code-menu-wrap-btn";
    if (wrapped) wrapBtn.classList.add("active");
    wrapBtn.innerHTML = (wrapped ? iconOneLine : iconWrap) +
        `<span>${wrapped ? docsT("docs.codeOneLine", "One line") : docsT("docs.codeWrap", "Wrap text")}</span>`;
    wrapBtn.addEventListener("mousedown", (e) => { e.preventDefault(); e.stopPropagation(); });
    wrapBtn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        onWrap(); // applyWrap() will call _syncCodeBlockMenu() to update this button
    });
    menu.appendChild(wrapBtn);

    const sep = document.createElement("div");
    sep.className = "docs-code-menu-sep";
    menu.appendChild(sep);

    const deleteBtn = document.createElement("button");
    deleteBtn.type = "button";
    deleteBtn.className = "docs-code-menu-btn docs-code-menu-delete-btn";
    deleteBtn.innerHTML = iconDelete + `<span>${docsT("docs.deleteBlock", "Delete")}</span>`;
    deleteBtn.addEventListener("mousedown", (e) => { e.preventDefault(); e.stopPropagation(); });
    deleteBtn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        _closeCodeBlockMenu();
        onDelete();
    });
    menu.appendChild(deleteBtn);

    // Position centered below the wrapper
    _codeMenuAnchor = wrapper;
    const rect = wrapper.getBoundingClientRect();
    menu.style.display = "block";
    const mw = menu.offsetWidth || 160;
    const mh = menu.offsetHeight || 80;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = rect.left + rect.width / 2 - mw / 2;
    let top = rect.bottom + 6;
    if (left + mw > vw - 8) left = vw - mw - 8;
    if (left < 8) left = 8;
    if (top + mh > vh - 8) top = rect.top - mh - 6;
    menu.style.left = left + "px";
    menu.style.top = top + "px";
}

function _syncCodeBlockMenu(wrapper, wrapped, iconWrap, iconOneLine) {
    if (_codeMenuAnchor !== wrapper) return;
    const menu = document.getElementById("docs-code-block-menu");
    if (!menu || menu.style.display === "none") return;
    const wrapBtn = menu.querySelector(".docs-code-menu-wrap-btn");
    if (!wrapBtn) return;
    wrapBtn.classList.toggle("active", wrapped);
    wrapBtn.innerHTML = (wrapped ? iconOneLine : iconWrap) +
        `<span>${wrapped ? docsT("docs.codeOneLine", "One line") : docsT("docs.codeWrap", "Wrap text")}</span>`;
}

// ── DocsCodeBlock ──────────────────────────────────────────────────────────
// Custom code-block NodeView — same architecture as DocsImage (see
// docs/guides/NODEVIEW_TABLE_JUMP_BUG.md for why this pattern is required).
//
// Layout (flex-wrap):
//   [pre (flex:1, scrollable)] [view-col: wrap+copy — view mode only]
//   [edit-bar: wrap+delete    — edit mode only, full-width row below  ]
//
// The pre and both action areas are siblings, never nested inside each other.
const DocsCodeBlock = Node.create({
    name: "codeBlock",
    group: "block",
    content: "text*",
    marks: "",
    code: true,
    defining: true,

    addAttributes() {
        return {
            language: {
                default: null,
                parseHTML: (el) => el.querySelector("code")?.dataset.language ?? null,
                renderHTML: (attrs) => attrs.language ? { "data-language": attrs.language } : {}
            },
            wrap: {
                default: false,
                parseHTML: (el) => el.hasAttribute("data-wrap"),
                renderHTML: (attrs) => attrs.wrap ? { "data-wrap": "" } : {}
            }
        };
    },

    parseHTML() {
        return [{ tag: "pre", preserveWhitespace: "full" }];
    },

    renderHTML({ HTMLAttributes }) {
        return ["pre", mergeAttributes(HTMLAttributes, { spellcheck: "false" }), ["code", 0]];
    },

    addKeyboardShortcuts() {
        return {
            "Mod-Alt-c": () => this.editor.commands.toggleCodeBlock()
        };
    },

    addCommands() {
        return {
            toggleCodeBlock: (attrs) => ({ commands }) =>
                commands.toggleNode(this.name, "paragraph", attrs),
            setCodeBlock: (attrs) => ({ commands }) =>
                commands.setNode(this.name, attrs),
        };
    },

    addNodeView() {
        return ({ node, editor, getPos }) => {
            // ── SVG icons ───────────────────────────────────────────────
            const iconWrap =
                `<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">` +
                `<line x1="1" y1="3.5" x2="15" y2="3.5"/>` +
                `<path d="M1 8h9.5a2 2 0 0 1 0 4H7"/>` +
                `<polyline points="5 10.5 7 12.5 5 14.5"/>` +
                `</svg>`;
            const iconOneLine =
                `<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">` +
                `<line x1="1" y1="8" x2="15" y2="8"/>` +
                `</svg>`;
            const iconCopy =
                `<svg class="docs-code-copy-icon" viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">` +
                `<rect x="5" y="2" width="9" height="11" rx="1.5"/>` +
                `<path d="M3 4H2a1 1 0 0 0-1 1v9a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1v-1"/>` +
                `</svg>` +
                `<svg class="docs-code-check-icon" viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">` +
                `<polyline points="2.5 8.5 6.5 12.5 13.5 4.5"/>` +
                `</svg>`;
            const iconDelete =
                `<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">` +
                `<polyline points="2 4 14 4"/><path d="M5 4V2h6v2"/><path d="M6 7v5M10 7v5"/>` +
                `<path d="M3 4l1 9a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1l1-9"/>` +
                `</svg>`;

            // ── Wrapper (NodeView root) ──────────────────────────────────
            const wrapper = document.createElement("div");
            wrapper.className = "docs-code-block-wrap";

            // ── <pre> + contentDOM ───────────────────────────────────────
            const pre = document.createElement("pre");
            pre.className = "docs-code-block";
            pre.setAttribute("spellcheck", "false");
            const code = document.createElement("code");
            pre.appendChild(code);
            wrapper.appendChild(pre);

            // ── View-mode right column (wrap + copy) ─────────────────────
            // Always visible in view mode; hidden in edit mode via CSS.
            // Dedicated right column — never overlaps code text.
            const viewCol = document.createElement("div");
            viewCol.className = "docs-code-view-col";
            viewCol.setAttribute("contenteditable", "false");

            const viewWrapBtn = document.createElement("button");
            viewWrapBtn.type = "button";
            viewWrapBtn.className = "docs-code-view-btn docs-code-view-wrap-btn";
            viewWrapBtn.setAttribute("aria-label", docsT("docs.codeWrap", "Toggle wrap"));
            viewWrapBtn.title = docsT("docs.codeWrap", "Wrap text");
            viewWrapBtn.innerHTML = iconWrap;
            viewCol.appendChild(viewWrapBtn);

            const viewCopyBtn = document.createElement("button");
            viewCopyBtn.type = "button";
            viewCopyBtn.className = "docs-code-view-btn docs-code-view-copy-btn";
            viewCopyBtn.setAttribute("aria-label", docsT("docs.copyCode", "Copy"));
            viewCopyBtn.title = docsT("docs.copyCode", "Copy code");
            viewCopyBtn.innerHTML = iconCopy;
            viewCol.appendChild(viewCopyBtn);

            wrapper.appendChild(viewCol);

            // ── Shared state: wrap toggle ────────────────────────────────
            let wrapped = !!node.attrs.wrap;
            let _persistingWrap = false;

            function applyWrap(on, persist = true) {
                wrapped = on;
                if (on) {
                    pre.setAttribute("data-wrap", "");
                    viewWrapBtn.setAttribute("aria-label", docsT("docs.codeOneLine", "One line"));
                    viewWrapBtn.title = docsT("docs.codeOneLine", "One line");
                    viewWrapBtn.innerHTML = iconOneLine;
                    viewWrapBtn.classList.add("active");
                } else {
                    pre.removeAttribute("data-wrap");
                    viewWrapBtn.setAttribute("aria-label", docsT("docs.codeWrap", "Wrap text"));
                    viewWrapBtn.title = docsT("docs.codeWrap", "Wrap text");
                    viewWrapBtn.innerHTML = iconWrap;
                    viewWrapBtn.classList.remove("active");
                }
                // Sync popup button if open for this block
                _syncCodeBlockMenu(wrapper, wrapped, iconWrap, iconOneLine);
                // Persist to ProseMirror node attribute so it survives keystrokes.
                // Use setNodeMarkup by position — updateAttributes() targets the
                // current selection, which may not be in this block when called
                // from the popup (editor can be unfocused).
                if (persist && !_persistingWrap) {
                    _persistingWrap = true;
                    const pos = typeof getPos === "function" ? getPos() : null;
                    if (pos != null) {
                        const { state, dispatch } = editor.view;
                        const nodeAt = state.doc.nodeAt(pos);
                        if (nodeAt) {
                            dispatch(state.tr.setNodeMarkup(pos, null, { ...nodeAt.attrs, wrap: on }));
                        }
                    }
                    _persistingWrap = false;
                }
            }

            // Initial state from node attributes
            applyWrap(wrapped, false);

            // ── View-mode wrap toggle ────────────────────────────────────
            viewWrapBtn.addEventListener("click", (e) => {
                e.preventDefault();
                e.stopPropagation();
                applyWrap(!wrapped);
            });

            // ── View-mode copy ───────────────────────────────────────────
            viewCopyBtn.addEventListener("click", async (e) => {
                e.preventDefault();
                e.stopPropagation();
                const text = code.textContent ?? "";
                if (!text) return;
                try {
                    await navigator.clipboard.writeText(text);
                    viewCopyBtn.dataset.copied = "true";
                    window.setTimeout(() => { delete viewCopyBtn.dataset.copied; }, 1600);
                } catch (_) { /* silent */ }
            });

            // ── Edit-mode: click wrapper → open popup ────────────────────
            wrapper.addEventListener("mousedown", (e) => {
                // Only open in edit mode; ignore clicks on the view-col buttons
                if (viewCol.contains(e.target)) return;
                const canvas = wrapper.closest("[data-mode]");
                if (!canvas || canvas.dataset.mode !== "edit") return;
                // Don't swallow the event — let ProseMirror handle selection
                // but schedule popup open after the click settles
                window.setTimeout(() => {
                    _openCodeBlockMenu({
                        wrapper,
                        wrapped,
                        iconWrap,
                        iconOneLine,
                        iconDelete,
                        onWrap: () => applyWrap(!wrapped),
                        onDelete: () => {
                            const pos = typeof getPos === "function" ? getPos() : null;
                            if (pos == null) return;
                            editor.chain().focus().deleteRange({ from: pos, to: pos + node.nodeSize }).run();
                        }
                    });
                }, 0);
            });

            return {
                dom: wrapper,
                contentDOM: code,
                update: (newNode) => {
                    if (newNode.type !== node.type) return false;
                    // Sync wrap state if changed externally (e.g. undo/redo)
                    if (!_persistingWrap) {
                        const newWrap = !!newNode.attrs.wrap;
                        if (newWrap !== wrapped) applyWrap(newWrap, false);
                    }
                    return true;
                },
            };
        };
    }
});

function decorateCodeBlocks(element) {
    // No-op: DocsCodeBlock NodeView renders the class and toolbar directly.
    // Kept so syncDecorations callers don't throw.
}

function decorateTocBlocks(element) {
    if (!element) return;

    const seen = new Map();
    const items = Array.from(element.querySelectorAll("h1, h2, h3")).map((heading) => {
        const text = (heading.textContent || "").trim();
        if (!text) return null;
        const baseSlug = slugifyHeading(text);
        const count = seen.get(baseSlug) || 0;
        seen.set(baseSlug, count + 1);
        const slug = count === 0 ? baseSlug : `${baseSlug}-${count + 1}`;
        heading.id = `doc-heading-${slug}`;
        return {
            level: Number(heading.tagName.replace("H", "")) || 1,
            text,
            slug
        };
    }).filter(Boolean);

    element.querySelectorAll("[data-doc-toc-block]").forEach((container) => {
        container.replaceChildren();
        if (!items.length) {
            const empty = document.createElement("div");
            empty.className = "docs-toc-block__empty";
            empty.textContent = docsT("docs.tocEmpty", "Add headings to generate a table of contents.");
            container.appendChild(empty);
            return;
        }

        const title = document.createElement("div");
        title.className = "docs-toc-block__title";
        title.textContent = docsT("docs.tocTitle", "Table of contents");
        const list = document.createElement("div");
        list.className = "docs-toc-block__list";
        items.forEach((item) => {
            const button = document.createElement("button");
            button.type = "button";
            button.className = `docs-toc-block__item level-${item.level}`;
            button.dataset.docTocTarget = item.slug;
            button.textContent = item.text;
            list.appendChild(button);
        });
        container.append(title, list);
    });
}

function decorateChildListBlocks(element, pages) {
    if (!element) return;
    element.querySelectorAll("[data-doc-child-list-block]").forEach((container) => {
        container.replaceChildren();
        const title = document.createElement("div");
        title.className = "docs-child-list-block__title";
        title.textContent = docsT("docs.childListTitle", "Child pages");
        container.appendChild(title);

        if (!pages || !pages.length) {
            const empty = document.createElement("div");
            empty.className = "docs-child-list-block__empty";
            empty.textContent = docsT("docs.noChildPages", "No child pages yet.");
            container.appendChild(empty);
            return;
        }

        const list = document.createElement("ul");
        list.className = "docs-child-list-block__list";
        pages.forEach((page) => {
            const li = document.createElement("li");
            const a = document.createElement("a");
            a.href = `#docs?doc=${page.id}`;
            a.className = "docs-child-list-block__link";
            a.textContent = (page.icon ? page.icon + " " : "") + (page.title || docsT("docs.untitledPage", "Untitled"));
            li.appendChild(a);
            list.appendChild(li);
        });
        container.appendChild(list);
    });
}


function bindReadonlyTaskListHandler(element) {
    if (!element || element.dataset.docsTaskListBound === "true") return;

    element.addEventListener("change", (event) => {
        const input = event.target.closest("ul[data-type='taskList'] input[type='checkbox']");
        if (!input || !docsEditor || docsEditor.isEditable) return;

        const view = docsEditor.view;
        const pos = view.posAtDOM(input, 0);
        const $pos = view.state.doc.resolve(pos);

        for (let depth = $pos.depth; depth > 0; depth -= 1) {
            const node = $pos.node(depth);
            if (node?.type?.name !== "taskItem") continue;

            const nodePos = $pos.before(depth);
            const tr = view.state.tr.setNodeMarkup(nodePos, undefined, {
                ...node.attrs,
                checked: Boolean(input.checked)
            });
            view.dispatch(tr);
            syncDecorations(docsEditor);
            return;
        }
    });

    element.dataset.docsTaskListBound = "true";
}

function bindDocsImageViewer(element) {
    if (!element || element.dataset.docsImageViewerBound === "true") return;

    element.addEventListener("click", (event) => {
        const image = event.target.closest(DOCS_IMAGE_TAG);
        if (!image) return;
        if (image.closest(".docs-canvas")?.dataset.mode === "edit") return;
        if (typeof window.viewImageLegacy !== "function") return;

        event.preventDefault();
        event.stopPropagation();

        const src = image.getAttribute("src");
        if (!src) return;

        const title = image.getAttribute("alt")
            || image.getAttribute("title")
            || "";
        window.viewImageLegacy(src, title);
    });

    element.dataset.docsImageViewerBound = "true";
}

let _tocScrollObserver = null;

function getTocScrollContainer(element) {
    return document.getElementById("docs-page-content") ||
        element.closest(".page-content") ||
        document.documentElement;
}

function setupTocScrollSpy(element) {
    if (_tocScrollObserver) {
        _tocScrollObserver.disconnect();
        _tocScrollObserver = null;
    }

    const headings = Array.from(
        element.querySelectorAll("h1[id^='doc-heading-'], h2[id^='doc-heading-'], h3[id^='doc-heading-']")
    );
    if (!headings.length) return;

    const sc = getTocScrollContainer(element);

    _tocScrollObserver = new IntersectionObserver(
        (entries) => {
            // Pick the topmost heading that just entered the viewport
            const visible = entries
                .filter((e) => e.isIntersecting)
                .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
            if (!visible.length) return;

            const activeId = visible[0].target.id.replace("doc-heading-", "");
            element.querySelectorAll("[data-doc-toc-target]").forEach((btn) => {
                btn.classList.toggle("active", btn.dataset.docTocTarget === activeId);
            });
        },
        {
            root: sc === document.documentElement ? null : sc,
            rootMargin: "0px 0px -72% 0px",
            threshold: 0
        }
    );

    headings.forEach((h) => _tocScrollObserver.observe(h));
}

function bindTocHandler(element) {
    if (!element) return;

    // Bind click handler only once
    if (!element.dataset.docsTocBound) {
        element.addEventListener("click", (event) => {
            const button = event.target.closest("[data-doc-toc-target]");
            if (!button) return;

            event.preventDefault();
            event.stopPropagation();

            const slug = button.dataset.docTocTarget;
            const target = element.querySelector(`#doc-heading-${slug}`);
            if (!target) return;

            const sc = getTocScrollContainer(element);
            const scRect = sc.getBoundingClientRect();
            const tRect = target.getBoundingClientRect();
            // Scroll the container so heading lands 80px from the top (comfortable reading position)
            const scrollTo = tRect.top - scRect.top + sc.scrollTop - 80;

            sc.scrollTo({ top: Math.max(0, scrollTo), behavior: "smooth" });

            // Immediate visual feedback before scroll spy kicks in
            element.querySelectorAll("[data-doc-toc-target]").forEach((b) =>
                b.classList.toggle("active", b === button)
            );
        });
        element.dataset.docsTocBound = "true";
    }

    // Always refresh scroll spy — headings may have changed since last update
    setupTocScrollSpy(element);
}

function getTableColumnCount(tableNode) {
    let count = 0;
    tableNode?.forEach((rowNode) => {
        let rowCount = 0;
        rowNode?.forEach((cellNode) => {
            rowCount += Math.max(1, Number(cellNode?.attrs?.colspan) || 1);
        });
        count = Math.max(count, rowCount);
    });
    return count;
}

function arraysEqualNumbers(a, b) {
    if (a.length !== b.length) return false;
    for (let index = 0; index < a.length; index += 1) {
        if (Number(a[index]) !== Number(b[index])) return false;
    }
    return true;
}

function getAvailableTableWidth(wrapper) {
    if (!wrapper) return 0;
    const canvasBodyWidth = Math.max(0, wrapper.closest(".docs-canvas__body")?.clientWidth || 0);
    const parentWidth = Math.max(0, wrapper.parentElement?.clientWidth || 0);
    return canvasBodyWidth || parentWidth;
}

function applyTableColumnWidths(tr, tablePos, tableNode, targetWidths) {
    let changed = false;

    tableNode?.forEach((rowNode, rowOffset) => {
        let columnIndex = 0;
        rowNode?.forEach((cellNode, cellOffset) => {
            const colspan = Math.max(1, Number(cellNode?.attrs?.colspan) || 1);
            const currentWidths = Array.isArray(cellNode?.attrs?.colwidth) ? cellNode.attrs.colwidth.map((width) => Number(width) || 0) : [];
            const nextWidths = targetWidths.slice(columnIndex, columnIndex + colspan).map((width) => Math.max(40, Number(width) || 40));
            if (!arraysEqualNumbers(currentWidths, nextWidths)) {
                const cellPos = tablePos + 1 + rowOffset + 1 + cellOffset;
                tr = tr.setNodeMarkup(cellPos, undefined, {
                    ...cellNode.attrs,
                    colwidth: nextWidths
                });
                changed = true;
            }
            columnIndex += colspan;
        });
    });

    return { tr, changed };
}

function syncTableWrappers(_root) {
    // Intentional no-op. Table layout is governed entirely by CSS:
    //   .docs-page-content { min-width: 0; overflow-x: hidden }
    // Mutating table/wrapper inline styles here caused TipTap's resize handles
    // to jump, post-drag layout shifts, and ResizeObserver feedback loops.
    // TipTap manages its own col/table widths; CSS containment clips overflow.
}

function scheduleDocsLayoutSync() {
    if (typeof window === "undefined") return;

    if (docsLayoutSyncRafA) {
        window.cancelAnimationFrame(docsLayoutSyncRafA);
        docsLayoutSyncRafA = 0;
    }
    if (docsLayoutSyncRafB) {
        window.cancelAnimationFrame(docsLayoutSyncRafB);
        docsLayoutSyncRafB = 0;
    }

    docsLayoutSyncRafA = window.requestAnimationFrame(() => {
        docsLayoutSyncRafB = window.requestAnimationFrame(() => {
            docsLayoutSyncRafA = 0;
            docsLayoutSyncRafB = 0;
            if (!docsEditor?.view?.dom) return;
            if (docsEditor.view.dom.classList.contains("resize-cursor")) {
                scheduleDocsLayoutSync();
                return;
            }
            syncTableWrappers(docsEditor.view.dom);
        });
    });
}

/**
 * After a column resize drag ends, scale all table column widths proportionally
 * so the table never exceeds the available container width. This eliminates the
 * "snap back" effect caused by overflow-x:hidden hiding the overflowing columns
 * while the user is dragging, then revealing them as hidden when released.
 */
function clampAllTableWidths() {
    if (!docsEditor?.view) return;
    const { state } = docsEditor.view;
    let tr = state.tr;
    let changed = false;

    state.doc.descendants((node, pos) => {
        if (node.type.name !== "table") return;

        // Get the rendered DOM node for this table to measure available space.
        const tableDom = docsEditor.view.nodeDOM(pos);
        const wrapper = tableDom instanceof Element
            ? tableDom.closest(".tableWrapper")
            : (tableDom?.parentElement?.closest?.(".tableWrapper") ?? null);
        const availableWidth = getAvailableTableWidth(wrapper);
        if (!availableWidth) return;

        // Collect per-column widths from the first row's colwidth attributes.
        const firstRow = node.childCount > 0 ? node.child(0) : null;
        if (!firstRow) return;
        const widths = [];
        firstRow.forEach((cell) => {
            const colspan = Math.max(1, Number(cell.attrs?.colspan) || 1);
            const cellWidths = Array.isArray(cell.attrs?.colwidth)
                ? cell.attrs.colwidth.map((w) => Number(w) || 0)
                : Array(colspan).fill(0);
            for (let i = 0; i < colspan; i++) {
                widths.push(cellWidths[i] || 0);
            }
        });

        const totalWidth = widths.reduce((sum, w) => sum + w, 0);
        // Only clamp if the table actually overflows AND we have valid width data.
        if (!totalWidth || totalWidth <= availableWidth) return;

        const scale = availableWidth / totalWidth;
        const scaledWidths = widths.map((w) => Math.max(40, Math.round(w * scale)));

        const result = applyTableColumnWidths(tr, pos, node, scaledWidths);
        if (result.changed) {
            tr = result.tr;
            changed = true;
        }
    });

    if (changed) {
        docsEditor.view.dispatch(tr);
    }
}

// ─── DEBUG: Image-in-table-cell hover inspector ───────────────────────────────
// Enabled only when debugLogsEnabled is "true" in localStorage.
// Logs cell structure changes and height shifts to diagnose the "phantom
// empty line" that appears below images on hover.
let _tableImgDebugObserver = null;
function bindTableImageDebugObserver(editorRoot) {
    if (localStorage.getItem("debugLogsEnabled") !== "true") return;
    if (_tableImgDebugObserver) { _tableImgDebugObserver.disconnect(); _tableImgDebugObserver = null; }

    const tag = (el) => `${el.nodeName.toLowerCase()}${el.className ? "." + [...el.classList].join(".") : ""}`;
    const cellInfo = (cell) => {
        const rows = [...cell.children].map((ch) => {
            const cs = getComputedStyle(ch);
            return {
                el: tag(ch),
                offsetH: ch.offsetHeight,
                display: cs.display,
                lineH: cs.lineHeight,
                height: cs.height,
                minH: cs.minHeight,
                margin: cs.margin,
                html: ch.innerHTML.slice(0, 120)
            };
        });
        return { cellOffsetH: cell.offsetHeight, rowH: cell.closest("tr")?.offsetHeight, children: rows };
    };

    // MutationObserver: catch any DOM changes inside image cells
    _tableImgDebugObserver = new MutationObserver((muts) => {
        for (const m of muts) {
            const cell = (m.target instanceof Element ? m.target : null)?.closest("td, th");
            if (!cell?.querySelector(".docs-image-wrap")) continue;
            const nodeDesc = (n) => {
                if (!(n instanceof Element)) return n.nodeName;
                const cs = getComputedStyle(n);
                return `<${n.nodeName.toLowerCase()} class="${n.className}" display=${cs.display} position=${cs.position} offsetH=${n.offsetHeight} outerHTML="${n.outerHTML.slice(0, 80)}">`;
            };
            const addedDesc = [...m.addedNodes].map(nodeDesc).join(" | ");
            const removedDesc = [...m.removedNodes].map(nodeDesc).join(" | ");
            console.log(`[TableImgDebug] ${m.type} on <${cell.tagName}> cellH=${cell.offsetHeight} children=${cell.children.length}`);
            if (addedDesc) console.log(`  ADDED: ${addedDesc}`);
            if (removedDesc) console.log(`  REMOVED: ${removedDesc}`);
        }
    });
    _tableImgDebugObserver.observe(editorRoot, {
        childList: true, subtree: true, attributes: true, attributeOldValue: true
    });

    // mousemove: track cell height while cursor sweeps across image cell
    let _lastCellH = null;
    editorRoot.addEventListener("mousemove", (e) => {
        const cell = e.target instanceof Element ? e.target.closest("td, th") : null;
        if (!cell?.querySelector(".docs-image-wrap")) { _lastCellH = null; return; }
        const h = cell.offsetHeight;
        if (h !== _lastCellH) {
            _lastCellH = h;
            console.log("[TableImgDebug] Cell height changed on mousemove →", h, cellInfo(cell));
        }
    }, { passive: true });

    // mouseenter/leave on image wrap: snapshot before / after
    editorRoot.addEventListener("mouseover", (e) => {
        const wrap = e.target instanceof Element ? e.target.closest(".docs-image-wrap") : null;
        if (!wrap) return;
        const cell = wrap.closest("td, th");
        if (!cell) return;
        console.log("[TableImgDebug] mouseenter .docs-image-wrap", cellInfo(cell));
    }, { passive: true });

    editorRoot.addEventListener("mouseout", (e) => {
        const wrap = e.target instanceof Element ? e.target.closest(".docs-image-wrap") : null;
        if (!wrap) return;
        const cell = wrap.closest("td, th");
        if (!cell) return;
        console.log("[TableImgDebug] mouseleave .docs-image-wrap", cellInfo(cell));
    }, { passive: true });

    console.log("[TableImgDebug] Observer bound to", editorRoot.tagName);
}
// ──────────────────────────────────────────────────────────────────────────────

// ─── DEBUG: Preset-click → Column-Resize geometry sequence ───────────────────
// Enabled only when debugLogsEnabled is "true" in localStorage.
// Logs the full geometry state immediately after a preset click and again on the
// very next column-resize-handle pointerdown, so we can see which measurement
// is stale (PM colwidth, DOM colgroup, table/wrapper rect, scrollable class).
let _presetResizeDebugBound = false;

function bindPresetResizeDebugObserver(editorRoot) {
    if (localStorage.getItem("debugLogsEnabled") !== "true") return;
    if (_presetResizeDebugBound) return;
    _presetResizeDebugBound = true;

    const tag = "[PresetResizeDebug]";

    function snapshotTableGeo(imageWrap) {
        const cell    = imageWrap?.closest("td, th");
        const table   = imageWrap?.closest("table");
        const wrapper = table?.closest(".tableWrapper");
        if (!table) return null;

        // PM doc colwidth attrs from first row
        const pmColwidths = [];
        if (docsEditor?.view) {
            docsEditor.view.state.doc.descendants((node, pos) => {
                if (node.type.name !== "table") return;
                const tDom = docsEditor.view.nodeDOM(pos);
                if (tDom !== table && !tDom?.contains?.(table)) return;
                const firstRow = node.childCount > 0 ? node.child(0) : null;
                if (firstRow) firstRow.forEach((cn) => pmColwidths.push(cn.attrs?.colwidth ?? "unset"));
                return false;
            });
        }

        // DOM <col> rendered widths
        const colWidthsDom = [...(table.querySelectorAll("colgroup > col"))].map(
            (col) => Math.round(col.getBoundingClientRect().width)
        );

        const tableRect = table.getBoundingClientRect();
        const wrapRect  = wrapper?.getBoundingClientRect() ?? tableRect;
        const cellRect  = cell?.getBoundingClientRect() ?? null;
        const figRect   = imageWrap.getBoundingClientRect();

        return {
            pmColwidths,
            colWidthsDom,
            tableW:         Math.round(tableRect.width),
            wrapW:          Math.round(wrapRect.width),
            cellW:          cellRect ? Math.round(cellRect.width) : null,
            figW:           Math.round(figRect.width),
            scrollable:     wrapper?.classList.contains("tableWrapper--scrollable") ?? false,
        };
    }

    let _afterPresetGeo = null;
    let _expectingFirstResize = false;

    // ── A: Preset button click ── capture before + after-2rAF geometry ────────
    editorRoot.addEventListener("click", (e) => {
        const btn  = e.target instanceof Element ? e.target.closest(".docs-image-preset-btn") : null;
        if (!btn) return;
        const wrap = btn.closest(".docs-image-wrap");
        if (!wrap) return;

        console.log(tag, "PRESET CLICK — before commit", {
            label: btn.dataset.label,
            ...snapshotTableGeo(wrap),
        });

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                _afterPresetGeo = snapshotTableGeo(wrap);
                console.log(tag, "PRESET CLICK — after commit (2-rAF)", _afterPresetGeo);
                _expectingFirstResize = true;
            });
        });
    }, { capture: true });

    // ── B: First resize-handle pointerdown after a preset click ───────────────
    document.addEventListener("pointerdown", (e) => {
        if (!_expectingFirstResize) return;
        const handle = e.target instanceof Element ? e.target.closest(".column-resize-handle") : null;
        if (!handle) return;
        _expectingFirstResize = false;

        const cell    = handle.closest("td, th");
        const table   = handle.closest("table");
        const wrapper = table?.closest(".tableWrapper");
        const imgWrap = cell?.querySelector(".docs-image-wrap");

        const tableRect = table?.getBoundingClientRect();
        const wrapRect  = wrapper?.getBoundingClientRect() ?? tableRect;
        const cellRect  = cell?.getBoundingClientRect();

        const colWidthsDom = [...(table?.querySelectorAll("colgroup > col") ?? [])].map(
            (col) => Math.round(col.getBoundingClientRect().width)
        );

        const pmColwidths = [];
        if (docsEditor?.view && table) {
            docsEditor.view.state.doc.descendants((node, pos) => {
                if (node.type.name !== "table") return;
                const tDom = docsEditor.view.nodeDOM(pos);
                if (tDom !== table && !tDom?.contains?.(table)) return;
                const firstRow = node.childCount > 0 ? node.child(0) : null;
                if (firstRow) firstRow.forEach((cn) => pmColwidths.push(cn.attrs?.colwidth ?? "unset"));
                return false;
            });
        }

        const drift = _afterPresetGeo ? {
            tableW_drift: tableRect ? Math.round(tableRect.width) - _afterPresetGeo.tableW : null,
            wrapW_drift:  wrapRect  ? Math.round(wrapRect.width)  - _afterPresetGeo.wrapW  : null,
        } : null;

        console.log(tag, "FIRST RESIZE START (after preset)", {
            pointerX:       Math.round(e.clientX),
            cellRectRight:  cellRect  ? Math.round(cellRect.right)  : null,
            tableRectRight: tableRect ? Math.round(tableRect.right) : null,
            wrapRectRight:  wrapRect  ? Math.round(wrapRect.right)  : null,
            tableW:         tableRect ? Math.round(tableRect.width)  : null,
            wrapW:          wrapRect  ? Math.round(wrapRect.width)   : null,
            scrollable:     wrapper?.classList.contains("tableWrapper--scrollable") ?? false,
            pmColwidths,
            colWidthsDom,
            figW:           imgWrap ? Math.round(imgWrap.getBoundingClientRect().width) : null,
            drift_vs_after_preset: drift,
        });
    }, { capture: true });
}
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Draws the column resize indicator by setting a CSS variable --col-x on
 * .tableWrapper and toggling class .col-resizing. The ::after pseudo-element
 * defined in CSS then renders the blue line at that X within the wrapper.
 *
 * No injected DOM elements. No coordinate drift. Scrolls with the table
 * automatically since it is a pseudo-element of the wrapper itself.
 */
function setupColumnHandleOverlay(editorRoot) {
    if (!editorRoot || editorRoot._colOverlayObs) return;

    // Overlay is position:fixed so it can be placed exactly over the table without
    // worrying about any positioned ancestor. getBoundingClientRect() values are
    // used directly as fixed coords.
    const overlay = document.createElement("div");
    overlay.className = "docs-col-resize-overlay";
    document.body.appendChild(overlay);

    let _rafId = null;
    let _visible = false;

    function showLine(handleEl) {
        const cell    = handleEl.closest("td, th");
        const table   = handleEl.closest("table");
        if (!cell || !table) { hideLine(); return; }

        const tableRect   = table.getBoundingClientRect();
        const cellRect    = cell.getBoundingClientRect();

        // The .tableWrapper may have overflow-x:auto, making the <table> wider than
        // what's visible. Constrain the overlay to the wrapper's visible right edge
        // so the blue bar never escapes outside the visible table area.
        const wrapper     = table.closest(".tableWrapper");
        const wrapRect    = wrapper ? wrapper.getBoundingClientRect() : tableRect;
        const visWidth    = Math.round(Math.min(tableRect.width, wrapRect.right - tableRect.left));

        // position:fixed — use viewport-relative coords directly.
        overlay.style.left   = Math.round(tableRect.left)   + "px";
        overlay.style.top    = Math.round(tableRect.top)    + "px";
        overlay.style.width  = visWidth                     + "px";
        overlay.style.height = Math.round(tableRect.height) + "px";

        // X of the column border within the overlay (relative to table left edge).
        // Clamped to [0, visWidth] so the line never escapes the visible table area
        // (which would be clipped invisible by clip-path:inset on the overlay).
        const rawLineX = Math.round(cellRect.right - tableRect.left);
        overlay.style.setProperty("--col-line-x", Math.min(visWidth, Math.max(0, rawLineX)) + "px");

        if (!_visible) {
            overlay.classList.add("active");
            _visible = true;
        }
    }

    function hideLine() {
        if (_visible) {
            overlay.classList.remove("active");
            _visible = false;
        }
    }

    function rafUpdate() {
        if (_rafId) return;
        _rafId = requestAnimationFrame(() => {
            _rafId = null;
            const handle = editorRoot.querySelector(".column-resize-handle");
            if (handle) showLine(handle);
            else hideLine();
        });
    }

    const obs = new MutationObserver(rafUpdate);
    obs.observe(editorRoot, { childList: true, subtree: true });

    // Reposition during drag — listen on document so we keep updating even when
    // the mouse leaves editorRoot during a fast drag to the table edge.
    document.addEventListener("mousemove", rafUpdate, { passive: true });

    // Reposition on scroll — position:fixed coords are viewport-relative.
    window.addEventListener("scroll", rafUpdate, { capture: true, passive: true });

    // Cleanup when the editor is torn down.
    editorRoot._colOverlayObs = obs;
    editorRoot._colOverlayCleanup = () => {
        obs.disconnect();
        document.removeEventListener("mousemove", rafUpdate);
        window.removeEventListener("scroll", rafUpdate, true);
        overlay.remove();
        if (_rafId) { cancelAnimationFrame(_rafId); _rafId = null; }
    };
}

function bindDocsLayoutObserver(editor) {
    if (typeof ResizeObserver === "undefined") return;

    if (docsLayoutResizeObserver) {
        docsLayoutResizeObserver.disconnect();
        docsLayoutResizeObserver = null;
    }

    const root = editor?.view?.dom;
    if (!root) return;

    const observed = new Set();
    const targets = [
        root.closest(".docs-canvas__body"),
        root.closest(".docs-page-content")
    ].filter(Boolean);

    let docsLayoutResizeDebounce = null;
    docsLayoutResizeObserver = new ResizeObserver(() => {
        if (docsLayoutResizeDebounce) clearTimeout(docsLayoutResizeDebounce);
        docsLayoutResizeDebounce = setTimeout(() => {
            scheduleDocsLayoutSync();
        }, 100);
    });

    targets.forEach((target) => {
        if (observed.has(target)) return;
        observed.add(target);
        docsLayoutResizeObserver.observe(target);
    });

    if (!docsTableResizeReleaseHandler && typeof document !== "undefined" && typeof window !== "undefined") {
        // Set the flag ONLY when the drag starts on an actual resize handle.
        // This prevents clampAllTableWidths from firing after table menu actions
        // (Options / Rows / Columns buttons) which also live inside .tableWrapper.
        const onResizeStart = (event) => {
            const target = event?.target instanceof Element ? event.target : null;
            if (target?.closest(".column-resize-handle")) {
                docsColumnResizeInProgress = true;
            }
        };
        docsTableResizeReleaseHandler = () => {
            if (!docsColumnResizeInProgress) return;
            docsColumnResizeInProgress = false;
            if (!docsEditor?.view?.dom) return;
            // Wait one frame for TipTap to commit its resize transaction, then
            // clamp any table whose columns overflow the available container width.
            window.requestAnimationFrame(() => {
                clampAllTableWidths();
            });
        };
        document.addEventListener("pointerdown", onResizeStart);
        document.addEventListener("mousedown", onResizeStart);
        document.addEventListener("pointerup", docsTableResizeReleaseHandler);
        document.addEventListener("mouseup", docsTableResizeReleaseHandler);
    }

    setupColumnHandleOverlay(root);
}

export function refreshDocsEditorLayout() {
    if (!docsEditor?.view?.dom) return;
    scheduleDocsLayoutSync();
}

export function refreshDocsChildListBlocks(pages) {
    _docsChildPages = Array.isArray(pages) ? pages : [];
    const root = docsEditor?.view?.dom;
    if (root) {
        decorateChildListBlocks(root, _docsChildPages);
    }
}

function syncDecorations(editor) {
    const root = editor?.view?.dom;
    if (!root) return;
    decorateCodeBlocks(root);
    decorateTocBlocks(root);
    decorateChildListBlocks(root, _docsChildPages);
    syncTableWrappers(root);
    // Code block toolbar is handled by DocsCodeBlock NodeView (no binding needed here)
    bindReadonlyTaskListHandler(root);
    bindDocsImageViewer(root);
    // Bind TOC handler on the outer container (not .ProseMirror) so it works
    // in view mode where [contenteditable] has pointer-events:none
    const tocContainer = docsEditorElement || root;
    bindTocHandler(tocContainer);
    scheduleDocsLayoutSync();
}

export function getDocsEditorTableState() {
    if (!docsEditor) return { active: false };
    const active = docsEditor.isActive("table");
    if (!active) return { active: false };

    const context = getActiveTableContext(docsEditor.state);
    const tableNode = context?.tableNode || null;
    const headerRow = Boolean(
        tableNode?.childCount > 0
        && tableNode.child(0)?.childCount > 0
        && tableNode.child(0).child(0)?.type?.name === "tableHeader"
    );
    let headerColumn = false;
    if (tableNode?.childCount) {
        headerColumn = true;
        tableNode.forEach((rowNode) => {
            if (!rowNode?.childCount) return;
            if (rowNode.child(0)?.type?.name !== "tableHeader") {
                headerColumn = false;
            }
        });
    }

    return {
        active: true,
        headerRow,
        headerColumn,
        numberedColumn: tableHasAutoNumberColumn(docsEditor.state)
    };
}

function getActiveStatusChipSelection(state) {
    const { selection } = state;
    if (selection?.node?.type?.name === "statusChip") {
        return {
            node: selection.node,
            pos: selection.from
        };
    }

    const { $from } = selection;
    if ($from?.nodeBefore?.type?.name === "statusChip") {
        return {
            node: $from.nodeBefore,
            pos: $from.pos - $from.nodeBefore.nodeSize
        };
    }
    if ($from?.nodeAfter?.type?.name === "statusChip") {
        return {
            node: $from.nodeAfter,
            pos: $from.pos
        };
    }
    return null;
}

export function getDocsEditorStatusChipState() {
    if (!docsEditor) return { active: false };
    const activeChip = getActiveStatusChipSelection(docsEditor.state);
    if (!activeChip) return { active: false };
    return {
        active: true,
        label: String(activeChip.node.attrs.label || "STATUS").trim() || "STATUS",
        tone: STATUS_CHIP_TONES.has(activeChip.node.attrs.tone) ? activeChip.node.attrs.tone : "grey"
    };
}

export function getDocsEditorLinkState() {
    if (!docsEditor) return { active: false, href: '' };
    const active = docsEditor.isActive('link');
    const href = active ? (docsEditor.getAttributes('link').href || '') : '';
    return { active, href };
}

export function getDocsCalloutState() {
    if (!docsEditor) return { active: false };
    const { $from } = docsEditor.state.selection;
    for (let d = $from.depth; d > 0; d--) {
        const node = $from.node(d);
        if (node.type.name === "callout") {
            return {
                active: true,
                tone: CALLOUT_TYPES.has(node.attrs.tone) ? node.attrs.tone : "info",
                icon: node.attrs.icon !== false
            };
        }
    }
    return { active: false };
}

export function getDocsEditorTableWrapper() {
    if (!docsEditorElement) return null;
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return null;
    const node = sel.getRangeAt(0).commonAncestorContainer;
    const el = node.nodeType === 1 ? node : node.parentElement;
    const cell = el?.closest("td, th");
    return cell?.closest(".tableWrapper") || null;
}

function extractNodeText(node) {
    if (!node) return "";
    // Atom inline nodes (e.g. status chip) — use label attr, not textContent
    if (node.isAtom && node.attrs?.label) return node.attrs.label;
    if (node.isText) return node.text || "";
    let text = "";
    node.forEach((child) => { text += extractNodeText(child); });
    return text;
}

function getActiveTableNode(state) {
    const { $from } = state.selection;
    let tableNode = null;
    for (let depth = $from.depth; depth > 0; depth--) {
        const node = $from.node(depth);
        if (node.type.name === "table") { tableNode = node; break; }
    }
    return tableNode;
}

function getActiveTablePlainText(tableNode) {
    if (!tableNode) return "";
    const rows = [];
    tableNode.forEach((row) => {
        const cells = [];
        row.forEach((cell) => {
            const text = extractNodeText(cell).replace(/\t/g, " ");
            cells.push(text);
        });
        rows.push(cells.join("\t"));
    });
    return rows.join("\n");
}

function copyComputedStyles(source, target, propertyNames) {
    if (!(source instanceof Element) || !(target instanceof Element)) return;
    const computed = window.getComputedStyle(source);
    propertyNames.forEach((property) => {
        const value = computed.getPropertyValue(property);
        if (value) {
            target.style.setProperty(property, value);
        }
    });
}

function createClipboardTableHtml(wrapper) {
    if (!(wrapper instanceof HTMLElement)) return "";

    const clone = wrapper.cloneNode(true);
    clone.querySelectorAll(".column-resize-handle").forEach((node) => node.remove());
    clone.querySelectorAll(".selectedCell").forEach((node) => node.classList.remove("selectedCell"));
    clone.removeAttribute("contenteditable");

    const sourceElements = [wrapper, ...wrapper.querySelectorAll("*")];
    const cloneElements = [clone, ...clone.querySelectorAll("*")];
    const sharedLength = Math.min(sourceElements.length, cloneElements.length);
    const propertyNames = [
        "display",
        "width",
        "min-width",
        "max-width",
        "border-collapse",
        "table-layout",
        "border-spacing",
        "background-color",
        "color",
        "font-family",
        "font-size",
        "font-style",
        "font-weight",
        "line-height",
        "text-align",
        "vertical-align",
        "white-space",
        "word-break",
        "overflow-wrap",
        "padding",
        "padding-top",
        "padding-right",
        "padding-bottom",
        "padding-left",
        "margin",
        "border",
        "border-top",
        "border-right",
        "border-bottom",
        "border-left",
        "border-radius",
        "list-style-type"
    ];

    for (let index = 0; index < sharedLength; index += 1) {
        copyComputedStyles(sourceElements[index], cloneElements[index], propertyNames);
    }

    return clone.outerHTML;
}

function closeDocsTableMenu() {
    const menu = document.getElementById('docs-table-menu');
    if (!menu) return;
    menu.querySelectorAll('[data-table-menu-dropdown]').forEach((dropdown) => {
        dropdown.hidden = true;
    });
    menu.querySelectorAll('[data-table-menu-toggle]').forEach((toggle) => {
        toggle.setAttribute('aria-expanded', 'false');
    });
}

export async function copyDocsTableAsText() {
    if (!docsEditor) return;
    const tableNode = getActiveTableNode(docsEditor.state);
    if (!tableNode) return;

    const plainText = getActiveTablePlainText(tableNode);
    const tableWrapper = getDocsEditorTableWrapper();
    const html = createClipboardTableHtml(tableWrapper);

    if (html && typeof ClipboardItem !== "undefined" && navigator.clipboard?.write) {
        const item = new ClipboardItem({
            "text/plain": new Blob([plainText], { type: "text/plain" }),
            "text/html": new Blob([html], { type: "text/html" })
        });
        try {
            await navigator.clipboard.write([item]);
            showSuccessNotification(docsT('docs.table.copied', 'Table copied to clipboard'));
            closeDocsTableMenu();
            return;
        } catch {
            // Fall back to plain text clipboard write below.
        }
    }

    navigator.clipboard.writeText(plainText).then(() => {
        showSuccessNotification(docsT('docs.table.copied', 'Table copied to clipboard'));
        closeDocsTableMenu();
    }).catch(() => {});
}

function getDocsRawUrlReplacements(doc, schema) {
    const replacements = [];
    const linkChipType = schema.nodes.linkChip;
    if (!linkChipType) return replacements;

    doc.descendants((node, pos, parent) => {
        if (!node?.isText || !node.text) return true;
        if (parent?.type?.name === "codeBlock") return true;
        if (node.marks?.some((mark) => mark?.type?.name === "code")) return true;

        DOCS_LINK_URL_REGEX.lastIndex = 0;
        let match = DOCS_LINK_URL_REGEX.exec(node.text);
        while (match) {
            const url = match[0];
            const from = pos + match.index;
            const to = from + url.length;
            replacements.push({ from, to, node: linkChipType.create(buildDocsLinkChipAttrs(url)) });
            match = DOCS_LINK_URL_REGEX.exec(node.text);
        }
        return true;
    });

    return replacements.sort((a, b) => b.from - a.from);
}

function convertRawUrlsInDocsEditorNow() {
    if (!docsEditor?.state?.schema?.nodes?.linkChip) return false;
    const replacements = getDocsRawUrlReplacements(docsEditor.state.doc, docsEditor.state.schema);
    if (replacements.length === 0) return false;

    let tr = docsEditor.state.tr;
    replacements.forEach(({ from, to, node }) => {
        tr = tr.replaceWith(from, to, node);
    });
    docsEditor.view.dispatch(tr);
    return true;
}

function replaceTrailingUrlWithDocsChip() {
    if (!docsEditor?.state?.schema?.nodes?.linkChip) return false;
    const { selection } = docsEditor.state;
    if (!selection.empty) return false;

    const parent = selection.$from.parent;
    if (!parent?.isTextblock) return false;

    const textBefore = parent.textBetween(0, selection.$from.parentOffset, "\n", "\n");
    const match = textBefore.match(DOCS_LINK_URL_AT_END_REGEX);
    if (!match) return false;

    const url = match[1];
    const from = selection.from - url.length;
    const to = selection.from;
    const chipNode = docsEditor.state.schema.nodes.linkChip.create(buildDocsLinkChipAttrs(url));

    let tr = docsEditor.state.tr.replaceWith(from, to, chipNode);
    const insertPos = from + chipNode.nodeSize;
    tr = tr.insertText(" ", insertPos);
    tr = tr.setSelection(tr.selection.constructor.near(tr.doc.resolve(insertPos + 1)));
    docsEditor.view.dispatch(tr);
    return true;
}

function bindDocsEditorKeydownHandler() {
    if (!docsEditorElement) return;
    if (docsEditorKeydownHandler) {
        docsEditorElement.removeEventListener("keydown", docsEditorKeydownHandler);
    }
    docsEditorKeydownHandler = (event) => {
        if (!docsEditor) return;
        if (event.key === " " || event.key === "Tab") {
            const replaced = replaceTrailingUrlWithDocsChip();
            if (replaced) {
                event.preventDefault();
                event.stopPropagation();
                return;
            }
        }
        if (event.key === "Enter") {
            convertRawUrlsInDocsEditorNow();
        }
        if (event.key === "Delete") {
            const { state } = docsEditor;
            const { selection } = state;
            if (!selection.empty) return;
            const { $from } = selection;
            // Must be inside an empty paragraph (no real content)
            if ($from.parent.type.name !== "paragraph") return;
            if ($from.parent.content.size !== 0) return;
            // Must be inside a table cell
            let cellDepth = -1;
            for (let d = $from.depth; d > 0; d--) {
                const name = $from.node(d).type.name;
                if (name === "tableCell" || name === "tableHeader") {
                    cellDepth = d;
                    break;
                }
            }
            if (cellDepth === -1) return;
            event.preventDefault();
            const cell = $from.node(cellDepth);
            if (cell.childCount === 1) {
                // Cell is empty — nothing to delete, just absorb the keystroke
            } else {
                // Empty paragraph alongside other content → delete just this line
                const paraStart = $from.before($from.depth);
                docsEditor.view.dispatch(
                    state.tr.delete(paraStart, paraStart + $from.parent.nodeSize)
                );
            }
        }
    };
    docsEditorElement.addEventListener("keydown", docsEditorKeydownHandler);
}

const CustomLink = Link.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            class: {
                default: null,
            },
        };
    },
    renderHTML({ HTMLAttributes }) {
        return ['a', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
    },
});

function getReadonlyTableRowCells(row) {
    return Array.from(row?.children || []).filter((cell) => {
        const tagName = cell?.tagName;
        return tagName === "TH" || tagName === "TD";
    });
}

function getReadonlyTableColumnLabel(index) {
    return docsT(
        "docs.table.mobileColumnLabel",
        "Column {number}",
        { number: index + 1 }
    ).trim();
}

// ── Mobile table readonly normalizer ───────────────────────
// On mobile, view-mode tables render as stacked cards. Normalize every readonly
// table so CSS can hide the true header row, keep body-row headers visible, and
// populate data-label attributes for the stacked layout. Idempotent by design.
export function normalizeReadonlyDocsTables(root) {
    if (!root || typeof root.querySelectorAll !== "function") return;
    const tables = root.matches?.("table")
        ? [root]
        : Array.from(root.querySelectorAll("table"));

    tables.forEach((table) => {
        const rows = Array.from(table.rows || []);
        const firstRow = rows[0];
        if (!firstRow) return;

        const firstRowCells = getReadonlyTableRowCells(firstRow);
        const hasTrueHeaderRow = firstRowCells.length > 0
            && firstRowCells.every((cell) => cell.tagName === "TH");
        const headerLabels = hasTrueHeaderRow
            ? firstRowCells.map((cell, index) => {
                const text = String(cell.textContent || "").trim();
                return text || getReadonlyTableColumnLabel(index);
            })
            : [];

        rows.forEach((row, rowIndex) => {
            const isHeaderRow = hasTrueHeaderRow && rowIndex === 0;
            row.dataset.docsMobileRow = isHeaderRow ? "header" : "data";

            const cells = getReadonlyTableRowCells(row);
            cells.forEach((cell, cellIndex) => {
                const label = isHeaderRow
                    ? ""
                    : (hasTrueHeaderRow
                        ? (headerLabels[cellIndex] || getReadonlyTableColumnLabel(cellIndex))
                        : getReadonlyTableColumnLabel(cellIndex));

                if (label) {
                    cell.setAttribute("data-label", label);
                } else {
                    cell.removeAttribute("data-label");
                }
            });
        });
    });
}

export function initializeDocsEditor({
    editorSelector,
    placeholder = "",
    onCreate = null,
    onUpdate = null,
    onFocus = null,
    onBlur = null,
    onSelectionUpdate = null,
    onCheckboxChange = null,
    onEmbedSave = null,
    getEmbedData = null
} = {}) {
    if (typeof document === "undefined") return null;

    const editorElement = typeof editorSelector === "string"
        ? document.querySelector(editorSelector)
        : editorSelector;
    if (!editorElement) return null;

    docsEditorHooks = {
        onCreate: typeof onCreate === "function" ? onCreate : null,
        onUpdate: typeof onUpdate === "function" ? onUpdate : null,
        onFocus: typeof onFocus === "function" ? onFocus : null,
        onBlur: typeof onBlur === "function" ? onBlur : null,
        onSelectionUpdate: typeof onSelectionUpdate === "function" ? onSelectionUpdate : null,
        onCheckboxChange: typeof onCheckboxChange === "function" ? onCheckboxChange : null,
        onEmbedSave: typeof onEmbedSave === "function" ? onEmbedSave : null,
        getEmbedData: typeof getEmbedData === "function" ? getEmbedData : null
    };

    docsEditorElement = editorElement;

    if (docsEditor) {
        docsEditor.destroy();
        docsEditor = null;
    }

    docsEditor = new Editor({
        element: editorElement,
        editorProps: {
            attributes: {
                class: "docs-editor-content"
            },
            handleDOMEvents: {
                // Prevent cursor snap near inline atom nodes (chips).
                // ProseMirror's updateState only syncs DOM selection when hasFocus()
                // is true. Focusing BEFORE dispatch ensures the DOM selection is
                // applied in the same synchronous tick, eliminating the 20 ms gap
                // that caused the visible snap.
                mousedown(view, event) {
                    if (!view.editable) return false;
                    const hit = view.posAtCoords({ left: event.clientX, top: event.clientY });
                    if (!hit) return false;
                    const $pos = view.state.doc.resolve(hit.pos);
                    const before = $pos.nodeBefore;
                    const after = $pos.nodeAfter;
                    const nearAtom =
                        (before && before.isAtom && before.isInline) ||
                        (after  && after.isAtom  && after.isInline);
                    if (!nearAtom) return false;
                    // Focus first so hasFocus() is true during dispatch.
                    view.dom.focus({ preventScroll: true });
                    view.dispatch(view.state.tr.setSelection(
                        TextSelection.create(view.state.doc, hit.pos)
                    ));
                    return true;
                }
            }
        },
        extensions: [
            StarterKit.configure({
                link: false,
                underline: false,
                codeBlock: false   // replaced by DocsCodeBlock NodeView below
            }),
            DocsCodeBlock,
            Underline,
            DocsLinkChip,
            DocsFileChip,
            CustomLink.configure({
                autolink: false,
                linkOnPaste: false,
                openOnClick: true,
                HTMLAttributes: {
                    target: "_blank",
                    rel: "noopener noreferrer"
                }
            }),
            Placeholder.configure({
                placeholder
            }),
            Table.configure({
                resizable: true
            }),
            TableRow,
            SortableTableHeader,
            TableCell,
            DocsTableSorting,
            PreventTableEdgeDelete,
            DocsAttachmentSupport,
            DocsImage,
            DocsFileCard,
            DocsYoutubeVideo,
            Callout,
            StatusChip,
            DateChip,
            TextAlign.configure({
                types: ["heading", "paragraph"]
            }),
            DocColumns,
            DocColumn,
            TableOfContentsBlock,
            ChildListBlock,
            ProgramTimelineBlock,
            TaskList.configure({ HTMLAttributes: { class: "docs-task-list" } }),
            TaskItem.configure({
                nested: true,
                // Let the readonly checkbox change event proceed; the delegated
                // DOM handler updates the actual taskItem node using posAtDOM.
                onReadOnlyChecked: () => true
            }),
            ChecklistWrapDecoration
        ],
        content: EMPTY_DOC,
        onCreate: ({ editor }) => {
            if (editor.view?.dom) {
                editor.view.dom.dataset.editMode = editor.isEditable ? "true" : "false";
            }
            syncDecorations(editor);
            bindDocsLayoutObserver(editor);
            bindTableImageDebugObserver(editor.view?.dom);
            bindPresetResizeDebugObserver(editor.view?.dom);
            if (!editor.isEditable) normalizeReadonlyDocsTables(editor.view?.dom);
            if (docsEditorHooks.onCreate) {
                docsEditorHooks.onCreate(editor.getJSON());
            }
        },
        onUpdate: ({ editor }) => {
            syncDecorations(editor);
            if (!editor.isEditable) normalizeReadonlyDocsTables(editor.view?.dom);
            if (docsEditorHooks.onUpdate) {
                docsEditorHooks.onUpdate(editor.getJSON());
            }
            // In read-only mode the only possible content change is a checkbox toggle
            // (via TipTap's onReadOnlyChecked). Fire the hook so callers can auto-save.
            if (!editor.isEditable && docsEditorHooks.onCheckboxChange) {
                docsEditorHooks.onCheckboxChange(editor.getJSON());
            }
        },
        onFocus: ({ editor }) => {
            if (docsEditorHooks.onFocus) {
                docsEditorHooks.onFocus(editor.getJSON());
            }
        },
        onBlur: ({ editor }) => {
            if (docsEditorHooks.onBlur) {
                docsEditorHooks.onBlur(editor.getJSON());
            }
        },
        onSelectionUpdate: ({ editor }) => {
            if (docsEditorHooks.onSelectionUpdate) {
                docsEditorHooks.onSelectionUpdate(editor);
            }
        }
    });

    syncDecorations(docsEditor);
    prewarmEmojiPicker();
    bindDocsEditorKeydownHandler();
    return docsEditor;
}

export function destroyDocsEditor() {
    if (!docsEditor) return;
    if (docsLayoutResizeObserver) {
        docsLayoutResizeObserver.disconnect();
        docsLayoutResizeObserver = null;
    }
    if (typeof window !== "undefined") {
        if (docsLayoutSyncRafA) window.cancelAnimationFrame(docsLayoutSyncRafA);
        if (docsLayoutSyncRafB) window.cancelAnimationFrame(docsLayoutSyncRafB);
    }
    if (docsEditorKeydownHandler && docsEditorElement) {
        docsEditorElement.removeEventListener("keydown", docsEditorKeydownHandler);
        docsEditorKeydownHandler = null;
    }
    if (docsTableResizeReleaseHandler && typeof document !== "undefined") {
        document.removeEventListener("pointerup", docsTableResizeReleaseHandler);
        document.removeEventListener("mouseup", docsTableResizeReleaseHandler);
        docsTableResizeReleaseHandler = null;
    }
    docsLayoutSyncRafA = 0;
    docsLayoutSyncRafB = 0;
    docsEditor.destroy();
    docsEditor = null;
    docsEditorElement = null;
}

export function setDocsEditorContent(docJson = EMPTY_DOC) {
    if (!docsEditor) return false;
    // Loading a saved document must start a new editing session. Tiptap's
    // setContent command is a full-document replacement that is otherwise
    // recorded by ProseMirror history, making a first Undo erase the page.
    // Recreating editor state with the same plugins preserves behavior while
    // clearing both Undo and Redo stacks.
    const nextDoc = docsEditor.schema.nodeFromJSON(normalizeDocJson(docJson));
    const nextState = EditorState.create({
        schema: docsEditor.state.schema,
        doc: nextDoc,
        plugins: docsEditor.state.plugins
    });
    docsEditor.view.updateState(nextState);
    syncDecorations(docsEditor);
    syncTableWrappers(docsEditor.view.dom);
    if (!docsEditor.isEditable) normalizeReadonlyDocsTables(docsEditor.view.dom);
    return true;
}

export function getDocsEditorJSON() {
    if (!docsEditor) return normalizeDocJson(EMPTY_DOC);
    return docsEditor.getJSON();
}

export function getDocsEditorHistoryAvailability() {
    if (!docsEditor) return { undo: false, redo: false };
    return {
        undo: docsEditor.can().undo(),
        redo: docsEditor.can().redo()
    };
}

// Current text selection in the docs editor. Used by Kai's selection-scoped
// rewrite to send only the highlighted fragment. `text` preserves newlines so
// the model can see block structure; `empty` is true for a caret-only selection.
export function getDocsEditorSelectionInfo() {
    if (!docsEditor) return { empty: true, from: 0, to: 0, text: '' };
    const { from, to, empty } = docsEditor.state.selection;
    const text = empty ? '' : docsEditor.state.doc.textBetween(from, to, '\n', ' ');
    return { empty: !!empty, from, to, text: String(text || '') };
}

// Splice replacement blocks into a captured selection range as a single live
// transaction (so Undo reverts only this edit, like the status-chip path).
// Re-validates the range against the current doc — and, when expectedText is
// given, that the same text still occupies it — so a page that changed between
// generate and apply falls back to whole-doc replacement instead of corrupting.
// Returns true on success, false if the range/text no longer matches.
export function replaceDocsEditorSelection(range, nodesJson, expectedText) {
    if (!docsEditor) return false;
    const docSize = docsEditor.state.doc.content.size;
    const from = Number(range && range.from);
    const to = Number(range && range.to);
    if (!Number.isFinite(from) || !Number.isFinite(to)) return false;
    if (from < 0 || to > docSize || from >= to) return false;

    const normalize = (value) => String(value || '').replace(/\s+/g, ' ').trim();
    if (typeof expectedText === 'string' && expectedText.length) {
        const currentText = docsEditor.state.doc.textBetween(from, to, '\n', ' ');
        if (normalize(currentText) !== normalize(expectedText)) return false;
    }

    const content = Array.isArray(nodesJson) ? nodesJson : [nodesJson];
    if (!content.length) return false;
    docsEditor.chain().focus().insertContentAt({ from, to }, content).run();
    return true;
}

// Replace the WHOLE document body as a single live transaction (select-all then
// insert), so the swap is one undoable step. Used by Kai's whole-page rewrite so
// Ctrl+Z / the Undo button revert the entire Kai edit in one move, instead of the
// history-wiping persist+reload path. Marks the doc dirty via the editor's normal
// onUpdate hook; the normal Publish flow persists it.
export function replaceDocsEditorContentLive(nodesJson) {
    if (!docsEditor) return false;
    const content = Array.isArray(nodesJson) ? nodesJson : [nodesJson];
    if (!content.length) return false;
    docsEditor.chain().focus().selectAll().insertContent(content).run();
    return true;
}

// Convert matching table cells into statusChip nodes as ONE ProseMirror
// transaction on the live editor, so it becomes a single normal undo step
// (Undo removes exactly the chips it added) instead of a whole-document
// replace. `targets` is [{ columnHeader, mapping: { LABEL: tone } }]. Matching
// is case-insensitive on both header and label; already-chipped and non-matching
// cells are left untouched. Returns the number of cells converted.
export function applyDocsStatusChips(targets = []) {
    if (!docsEditor) return 0;
    const rawTargets = Array.isArray(targets) ? targets : [];
    if (!rawTargets.length) return 0;

    const { state } = docsEditor;
    const { doc, schema } = state;
    const statusChipType = schema.nodes.statusChip;
    const paragraphType = schema.nodes.paragraph;
    if (!statusChipType || !paragraphType) return 0;

    // headerLower -> Map(labelLower -> tone)
    const mappingByHeader = new Map();
    rawTargets.forEach((target) => {
        const header = String(target?.columnHeader || "").trim().toLowerCase();
        if (!header) return;
        const labelToTone = new Map();
        Object.entries(target?.mapping || {}).forEach(([label, tone]) => {
            const key = String(label || "").trim().toLowerCase();
            if (!key) return;
            const normalizedTone = String(tone || "").trim().toLowerCase();
            labelToTone.set(key, STATUS_CHIP_TONES.has(normalizedTone) ? normalizedTone : "grey");
        });
        if (labelToTone.size) mappingByHeader.set(header, labelToTone);
    });
    if (!mappingByHeader.size) return 0;

    const cellText = (cell) => String(cell.textContent || "").replace(/\s+/g, " ").trim();
    const cellHasChip = (cell) => {
        let found = false;
        cell.descendants((child) => {
            if (found) return false;
            if (child.type === statusChipType) { found = true; return false; }
            return true;
        });
        return found;
    };

    const replacements = [];
    doc.descendants((node, pos) => {
        if (node.type.name !== "table") return true;
        if (node.childCount < 2) return false;

        const headerText = [];
        node.child(0).forEach((cell) => {
            headerText.push(cellText(cell).toLowerCase());
        });

        node.forEach((row, rowOffset, rowIndex) => {
            if (rowIndex === 0) return;
            const rowPos = pos + 1 + rowOffset;
            row.forEach((cell, cellOffset, colIndex) => {
                const header = headerText[colIndex];
                const mapping = header ? mappingByHeader.get(header) : null;
                if (!mapping || cellHasChip(cell)) return;
                const label = cellText(cell);
                if (!label) return;
                const tone = mapping.get(label.toLowerCase());
                if (!tone) return;
                const cellPos = rowPos + 1 + cellOffset;
                replacements.push({
                    from: cellPos + 1,
                    to: cellPos + cell.nodeSize - 1,
                    label,
                    tone
                });
            });
        });
        return false;
    });

    if (!replacements.length) return 0;

    // Apply from the end backwards so earlier positions stay valid as we edit.
    replacements.sort((a, b) => b.from - a.from);
    const tr = state.tr;
    replacements.forEach(({ from, to, label, tone }) => {
        const chip = statusChipType.create({ label, tone });
        tr.replaceWith(from, to, paragraphType.create(null, chip));
    });
    docsEditor.view.dispatch(tr);
    return replacements.length;
}

export function focusDocsEditor() {
    if (!docsEditor) return false;
    return docsEditor.commands.focus();
}

/* ── Slash commands ───────────────────────────────────────────────────────────
   The "/" trigger only counts when it opens a word: at the start of the
   textblock or straight after whitespace. That way a URL ("http://x"), a date
   ("12/03") or plain division never opens the menu. Returns the query typed so
   far plus the document range covering "/query", so the caller can delete
   exactly that text before inserting a node. */
export function getDocsSlashContext() {
    if (!docsEditor || !docsEditor.isEditable) return null;

    const { selection } = docsEditor.state;
    if (!selection.empty) return null;

    const $from = selection.$from;
    if (!$from.parent.isTextblock) return null;
    // Inside a code block "/" is literal text, not a command.
    if ($from.parent.type.name === "codeBlock") return null;

    const textBefore = $from.parent.textBetween(0, $from.parentOffset, undefined, "￼");
    const match = /(?:^|\s)\/([^\s/]*)$/.exec(textBefore);
    if (!match) return null;

    const query = match[1] || "";
    const to = selection.from;
    const from = to - query.length - 1; // include the "/" itself
    return { query, from, to };
}

/** Viewport rect of the caret, for anchoring the slash menu. */
export function getDocsCaretRect() {
    if (!docsEditor?.view) return null;
    try {
        const coords = docsEditor.view.coordsAtPos(docsEditor.state.selection.from);
        return { left: coords.left, top: coords.top, bottom: coords.bottom };
    } catch (_) {
        return null;
    }
}

/** Remove the typed "/query" so the inserted node doesn't sit next to it. */
export function deleteDocsRange(from, to) {
    if (!docsEditor) return false;
    return docsEditor.chain().focus().deleteRange({ from, to }).run();
}

export function setDocsEditorEditable(editable) {
    if (!docsEditor) return;
    docsEditor.setEditable(editable);
    if (docsEditor.view?.dom) {
        docsEditor.view.dom.dataset.editMode = editable ? "true" : "false";
    }
    if (typeof document !== "undefined") {
        document.querySelectorAll(".docs-image-resize-handle").forEach((handle) => {
            if (editable) {
                handle.style.removeProperty("display");
            } else {
                handle.style.display = "none";
            }
        });
        document.querySelectorAll(".docs-image-wrap").forEach((wrapper) => {
            if (!editable) {
                wrapper.classList.remove("is-resizing");
            }
        });
    }
    if (!editable && docsEditor.view?.dom) {
        normalizeReadonlyDocsTables(docsEditor.view.dom);
    }
}

// ── Emoji picker ──────────────────────────────────────────────────────────
let _emojiPicker = null;
let _emojiPickerWrap = null;
let _emojiPickerPrewarmScheduled = false;
let _emojiPickerSelectHandler = null;
let _emojiPickerAnchorSelector = "[data-doc-command='emoji'], [data-doc-icon-trigger]";
let _docsThemeListenerBound = false;
let _docsLanguageListenerBound = false;

function getOrCreateEmojiPicker() {
    if (_emojiPickerWrap) return _emojiPickerWrap;

    _emojiPickerWrap = document.createElement("div");
    _emojiPickerWrap.className = "docs-emoji-picker-wrap";
    _emojiPickerWrap.style.display = "none";

    _emojiPicker = document.createElement("emoji-picker");
    _emojiPicker.setAttribute("class", "docs-emoji-picker");
    _emojiPicker.locale = "en";
    _emojiPicker.dataSource = DOCS_EMOJI_DATA_SOURCE;
    _emojiPicker.emojiVersion = 15.0;

    _emojiPicker.addEventListener("emoji-click", (e) => {
        const emoji = e.detail?.unicode;
        if (emoji && typeof _emojiPickerSelectHandler === "function") {
            _emojiPickerSelectHandler(emoji);
        } else if (emoji && docsEditor) {
            docsEditor.chain().focus().insertContent(emoji).run();
        }
        hideEmojiPicker();
    });

    _emojiPickerWrap.appendChild(_emojiPicker);
    document.body.appendChild(_emojiPickerWrap);

    // Only close when BOTH pointerdown AND pointerup were outside the picker.
    // This prevents drag-selection from inside the search box closing the picker
    // when the mouse momentarily exits the boundary.
    let _downOutside = false;
    document.addEventListener("pointerdown", (e) => {
        if (_emojiPickerWrap.style.display === "none") return;
        _downOutside = !_emojiPickerWrap.contains(e.target) &&
                       !e.target.closest(_emojiPickerAnchorSelector);
    });
    document.addEventListener("pointerup", (e) => {
        if (!_downOutside) return;
        if (_emojiPickerWrap.style.display === "none") return;
        if (!_emojiPickerWrap.contains(e.target) &&
            !e.target.closest(_emojiPickerAnchorSelector)) {
            hideEmojiPicker();
        }
        _downOutside = false;
    });

    return _emojiPickerWrap;
}

function prewarmEmojiPicker() {
    if (_emojiPickerPrewarmScheduled || typeof window === "undefined") return;
    _emojiPickerPrewarmScheduled = true;

    const warm = () => {
        fetch(DOCS_EMOJI_DATA_SOURCE, { cache: "force-cache" }).catch(() => {});
        getOrCreateEmojiPicker();
    };

    if (typeof window.requestIdleCallback === "function") {
        window.requestIdleCallback(warm, { timeout: 1200 });
        return;
    }

    window.setTimeout(warm, 250);
}

function hideEmojiPicker() {
    _emojiPickerSelectHandler = null;
    _emojiPickerAnchorSelector = "[data-doc-command='emoji'], [data-doc-icon-trigger]";
    if (_emojiPickerWrap) _emojiPickerWrap.style.display = "none";
}

function ensureDocsThemeListener() {
    if (_docsThemeListenerBound || typeof document === "undefined") return;
    _docsThemeListenerBound = true;

    document.addEventListener("nautilus:themechange", (event) => {
        const theme = event?.detail?.theme === "dark" ? "dark" : "light";
        if (_emojiPicker) {
            _emojiPicker.setAttribute("data-theme", theme);
        }
    });
}

function ensureDocsLanguageListener() {
    if (_docsLanguageListenerBound || typeof document === "undefined") return;
    _docsLanguageListenerBound = true;

    document.addEventListener("nautilus:languagechange", (event) => {
        const locale = event?.detail?.language === "es" ? "es" : "en";
        if (_emojiPicker) {
            _emojiPicker.locale = locale;
        }
    });
}

export function toggleDocsEmojiPicker(anchorEl, options = {}) {
    ensureDocsThemeListener();
    ensureDocsLanguageListener();
    const wrap = getOrCreateEmojiPicker();
    if (wrap.style.display !== "none") {
        hideEmojiPicker();
        return;
    }

    _emojiPickerSelectHandler = typeof options.onSelect === "function" ? options.onSelect : null;
    _emojiPickerAnchorSelector = options.anchorSelector || "[data-doc-command='emoji'], [data-doc-icon-trigger]";

    // Sync dark mode
    const isDark = document.documentElement.dataset.theme === "dark" ||
                   document.body.dataset.theme === "dark";
    _emojiPicker.setAttribute("data-theme", isDark ? "dark" : "light");
    _emojiPicker.locale = typeof window !== "undefined" && window.i18n?.getCurrentLanguage
        ? window.i18n.getCurrentLanguage()
        : "en";

    // Position: prefer below the button, flip up if too close to bottom
    const rect = anchorEl.getBoundingClientRect();
    const pickerH = 420;
    const spaceBelow = window.innerHeight - rect.bottom;
    const top = spaceBelow > pickerH
        ? rect.bottom + 6
        : rect.top - pickerH - 6;
    const left = Math.min(rect.left, window.innerWidth - 360);

    wrap.style.position = "fixed";
    wrap.style.top = `${Math.max(8, top)}px`;
    wrap.style.left = `${Math.max(8, left)}px`;
    wrap.style.display = "block";
    wrap.style.zIndex = "9999";
}

export function runDocsEditorCommand(command, param) {
    if (!docsEditor) return false;

    switch (command) {
        case "bold":
            return docsEditor.chain().focus().toggleBold().run();
        case "italic":
            return docsEditor.chain().focus().toggleItalic().run();
        case "underline":
            return docsEditor.chain().focus().toggleUnderline().run();
        case "strike":
            return docsEditor.chain().focus().toggleStrike().run();
        case "inlineCode":
            return docsEditor.chain().focus().toggleCode().run();
        case "link":
            if (docsEditor.isActive('link')) {
                return docsEditor.chain().focus().unsetLink().run();
            }
            return false; // caller opens link input sheet
        case "setLink":
            if (!param) return false;
            return docsEditor.chain().focus().setLink({ href: String(param) }).run();
        case "bulletList":
            return docsEditor.chain().focus().toggleBulletList().run();
        case "orderedList":
            return docsEditor.chain().focus().toggleOrderedList().run();
        case "heading":
            return docsEditor.chain().focus().toggleHeading({ level: Number(param) || 2 }).run();
        case "divider":
            return docsEditor.chain().focus().setHorizontalRule().run();
        case "codeBlock":
            return docsEditor.chain().focus().toggleCodeBlock().run();
        case "table": {
            const inserted = docsEditor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
            if (!inserted) return false;
            // Apply default column widths to ensure minimum width
            return applyDefaultTableColumnWidths(docsEditor);
        }
        case "deleteTable":
            return docsEditor.chain().focus().deleteTable().run();
        case "toggleHeaderRow": {
            const shouldRenumber = tableHasAutoNumberColumn(docsEditor.state);
            const toggled = docsEditor.chain().focus().toggleHeaderRow().run();
            if (!toggled) return false;
            return shouldRenumber ? fillFirstColumnWithNumbers(docsEditor) : true;
        }
        case "toggleHeaderColumn":
            return docsEditor.chain().focus().toggleHeaderColumn().run();
        case "addRowBefore": {
            const shouldRenumber = tableHasAutoNumberColumn(docsEditor.state);
            const added = docsEditor.chain().focus().addRowBefore().run();
            if (!added) return false;
            return shouldRenumber ? fillFirstColumnWithNumbers(docsEditor) : true;
        }
        case "addRowAfter": {
            const shouldRenumber = tableHasAutoNumberColumn(docsEditor.state);
            const added = docsEditor.chain().focus().addRowAfter().run();
            if (!added) return false;
            return shouldRenumber ? fillFirstColumnWithNumbers(docsEditor) : true;
        }
        case "deleteRow": {
            const shouldRenumber = tableHasAutoNumberColumn(docsEditor.state);
            const removed = docsEditor.chain().focus().deleteRow().run();
            if (!removed) return false;
            return shouldRenumber ? fillFirstColumnWithNumbers(docsEditor) : true;
        }
        case "addColumnBefore":
            return docsEditor.chain().focus().addColumnBefore().run();
        case "addColumnAfter":
            return docsEditor.chain().focus().addColumnAfter().run();
        case "deleteColumn":
            return docsEditor.chain().focus().deleteColumn().run();
        case "numberColumn": {
            if (tableHasAutoNumberColumn(docsEditor.state)) {
                return removeNumberedColumn(docsEditor);
            }
            return insertNumberedColumn(docsEditor);
        }
        case "distributeColumnsEvenly": {
            return distributeTableColumnsEvenly(docsEditor);
        }
        case "callout":
            return docsEditor.chain().focus().insertCallout(param || "info").run();
        case "updateCallout":
            return docsEditor.chain().focus().updateCalloutTone(param || "info").run();
        case "columns":
            return docsEditor.chain().focus().insertTwoColumns().run();
        case "toc":
            return docsEditor.chain().focus().insertTableOfContents().run();
        case "childList":
            return docsEditor.chain().focus().insertChildList().run();
        case "programTimeline":
            return docsEditor.chain().focus().insertProgramTimeline().run();
        case "statusChip":
            return docsEditor.chain().focus().insertStatusChip({
                label: typeof param === "object" ? param.label : "STATUS",
                tone: typeof param === "object" ? param.tone : "grey"
            }).run();
        case "updateStatusChip":
            return docsEditor.chain().focus().updateStatusChip({
                label: typeof param === "object" ? param.label : "STATUS",
                tone: typeof param === "object" ? param.tone : "grey"
            }).run();
        case "dateChip":
            return docsEditor.chain().focus().insertDateChip({
                date: typeof param === "string" ? param : new Date().toISOString().split("T")[0]
            }).run();
        case "updateDateChip":
            return docsEditor.chain().focus().updateDateChip({
                date: typeof param === "string" ? param : ""
            }).run();
        case "textAlign":
            return docsEditor.chain().focus().setTextAlign(param || "left").run();
        case "taskList":
            return docsEditor.chain().focus().toggleTaskList().run();
        case "undo":
            // Toolbar Undo/Redo must keep the reader's viewport stable. Tiptap
            // focuses by scrolling the stored selection into view by default;
            // after a table transaction that selection can be several rows away.
            return docsEditor.chain().focus(null, { scrollIntoView: false }).undo().run();
        case "redo":
            return docsEditor.chain().focus(null, { scrollIntoView: false }).redo().run();
        case "paragraph":
            return docsEditor.chain().focus().setParagraph().run();
        case "toggleCalloutIcon":
            return docsEditor.chain().focus().toggleCalloutIcon().run();
        default:
            return false;
    }
}

// ── Find & Replace helpers ────────────────────────────────────────────────────

function _buildFindRegex(query, caseSensitive) {
    const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(escaped, caseSensitive ? 'g' : 'gi');
}

function _collectTextMatches(doc, regex) {
    const matches = [];
    doc.descendants((node, pos) => {
        if (!node.isText) return;
        let m;
        regex.lastIndex = 0;
        while ((m = regex.exec(node.text)) !== null) {
            matches.push({ from: pos + m.index, to: pos + m.index + m[0].length });
        }
    });
    return matches;
}

export function findDocsTextMatches(query, caseSensitive = false) {
    if (!docsEditor || !query) return [];
    return _collectTextMatches(docsEditor.state.doc, _buildFindRegex(query, caseSensitive));
}

export function selectDocsMatch(from, to) {
    if (!docsEditor) return;
    docsEditor.chain().setTextSelection({ from, to }).scrollIntoView().run();
}

export function replaceDocsMatch(from, to, replacement) {
    if (!docsEditor) return;
    const content = replacement ? docsEditor.schema.text(replacement) : null;
    const tr = docsEditor.state.tr;
    if (content) {
        docsEditor.view.dispatch(tr.replaceWith(from, to, content));
    } else {
        docsEditor.view.dispatch(tr.delete(from, to));
    }
}

export function replaceAllDocsText(query, replacement, caseSensitive = false) {
    if (!docsEditor || !query) return 0;
    const matches = _collectTextMatches(docsEditor.state.doc, _buildFindRegex(query, caseSensitive));
    if (matches.length === 0) return 0;
    let tr = docsEditor.state.tr;
    // Reverse order so earlier positions stay valid after each replacement
    [...matches].reverse().forEach(({ from, to }) => {
        if (replacement) {
            tr = tr.replaceWith(from, to, docsEditor.schema.text(replacement));
        } else {
            tr = tr.delete(from, to);
        }
    });
    docsEditor.view.dispatch(tr);
    return matches.length;
}

// ─────────────────────────────────────────────────────────────────────────────

export function insertDocsImage({ src, alt = "", title = "", fileKey = "" } = {}) {
    if (!docsEditor || !src) return false;
    const result = docsEditor.chain().focus().setDocsImage({
        src,
        alt,
        title,
        fileKey,
        width: DOCS_IMAGE_DEFAULT_WIDTH
    }).run();
    // After inserting an atom block node, TipTap may leave a NodeSelection on it.
    // Subsequent insertContent calls would then replace the selected node instead of
    // appending after it. Move the cursor past the node to prevent overlap.
    if (result) {
        const { selection } = docsEditor.state;
        if (selection instanceof NodeSelection) {
            docsEditor.commands.setTextSelection(selection.$to.pos);
        }
    }
    return result;
}

export function insertDocsYoutubeVideo(value) {
    if (!docsEditor) return false;
    return docsEditor.chain().focus().setDocsYoutubeVideo(value).run();
}

export function insertDocsFileAttachment(attributes = {}, options = {}) {
    if (!docsEditor) return false;
    return docsEditor.chain().focus().insertDocsFileAttachment(attributes, options).run();
}

export function extractDocsToc(docJson) {
    const toc = [];
    const seen = new Map();
    const walk = (node) => {
        if (!node || typeof node !== "object") return;
        if (node.type === "heading") {
            const text = extractPlainText(node).trim();
            if (text) {
                const baseSlug = slugifyHeading(text);
                const count = seen.get(baseSlug) || 0;
                seen.set(baseSlug, count + 1);
                toc.push({
                    level: Number(node.attrs?.level) || 1,
                    text,
                    slug: count === 0 ? baseSlug : `${baseSlug}-${count + 1}`
                });
            }
        }
        if (Array.isArray(node.content)) {
            node.content.forEach(walk);
        }
    };

    walk(normalizeDocJson(docJson));
    return toc;
}

export function extractPlainText(node) {
    if (!node || typeof node !== "object") return "";
    // ProseMirror node: node.type is a NodeType object, not a string
    if (node.type && typeof node.type === "object" && typeof node.textContent === "string") {
        return node.textContent;
    }
    // Plain JSON node (used in TOC extraction from docJson)
    if (node.type === "text") return String(node.text || "");
    if (!Array.isArray(node.content)) return "";
    return node.content.map(extractPlainText).join("");
}

function slugifyHeading(value) {
    return String(value || "")
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "") || "section";
}

function getActiveTableContext(state) {
    const { $from } = state.selection;
    for (let depth = $from.depth; depth > 0; depth -= 1) {
        const node = $from.node(depth);
        if (node?.type?.name === "table") {
            return {
                tableNode: node,
                tablePos: $from.before(depth)
            };
        }
    }
    return null;
}

function insertNumberedColumn(editor) {
    const context = getActiveTableContext(editor.state);
    if (!context) return false;

    const { state } = editor;
    const { tableNode, tablePos } = context;
    const { schema } = state;
    const tableHeaderType = schema.nodes.tableHeader;
    const tableCellType = schema.nodes.tableCell;
    const paragraph = schema.nodes.paragraph;
    if (!paragraph || !tableCellType) return false;

    const headerRow = tableNode.childCount > 0
        && tableNode.child(0).childCount > 0
        && tableNode.child(0).child(0)?.type?.name === "tableHeader";

    let tr = state.tr;

    tableNode.forEach((rowNode, rowOffset, rowIndex) => {
        if (!rowNode?.childCount) return;
        const label = headerRow && rowIndex === 0 ? "#" : String(headerRow ? rowIndex : rowIndex + 1);
        const content = paragraph.create(null, schema.text(label));
        const isHeaderRow = rowNode.child(0).type.name === "tableHeader";
        const cellType = isHeaderRow && tableHeaderType ? tableHeaderType : tableCellType;
        const newCell = cellType.create({ colspan: 1, rowspan: 1, colwidth: [48] }, content);
        // Always insert at position 0 of the row, regardless of cursor column
        const rowContentPos = tr.mapping.map(tablePos + 1 + rowOffset + 1);
        tr = tr.insert(rowContentPos, newCell);
    });

    editor.view.dispatch(tr);
    return true;
}

function fillFirstColumnWithNumbers(editor) {
    const context = getActiveTableContext(editor.state);
    if (!context) return false;

    const { state } = editor;
    const { tableNode, tablePos } = context;
    const { schema } = state;
    const paragraph = schema.nodes.paragraph;
    if (!paragraph) return false;

    let tr = state.tr;
    const headerRow = tableNode.childCount > 0
        && tableNode.child(0).childCount > 0
        && tableNode.child(0).child(0)?.type?.name === "tableHeader";

    tableNode.forEach((rowNode, rowOffset, rowIndex) => {
        if (!rowNode?.childCount) return;
        const originalCellPos = tablePos + 1 + rowOffset + 1;
        const cellPos = tr.mapping.map(originalCellPos);
        const cellNode = rowNode.child(0);
        const label = headerRow && rowIndex === 0 ? "#" : String(headerRow ? rowIndex : rowIndex + 1);
        const contentNode = paragraph.create(null, schema.text(label));
        tr = tr.setNodeMarkup(cellPos, null, { ...cellNode.attrs, colwidth: [48] });
        tr = tr.replaceWith(cellPos + 1, cellPos + cellNode.nodeSize - 1, contentNode);
    });

    editor.view.dispatch(tr);
    return true;
}

function removeNumberedColumn(editor) {
    const context = getActiveTableContext(editor.state);
    if (!context) return false;

    const { state } = editor;
    const { tableNode, tablePos } = context;

    // Delete the first cell from every row (the # column)
    let tr = state.tr;
    tableNode.forEach((rowNode, rowOffset) => {
        if (!rowNode?.childCount) return;
        const cellStart = tr.mapping.map(tablePos + 1 + rowOffset + 1);
        const cellNode = tr.doc.nodeAt(cellStart);
        if (!cellNode) return;
        tr = tr.delete(cellStart, cellStart + cellNode.nodeSize);
    });

    editor.view.dispatch(tr);
    return true;
}

function distributeTableColumnsEvenly(editor) {
    const context = getActiveTableContext(editor.state);
    if (!context) return false;

    const { state, view } = editor;
    const { tableNode, tablePos } = context;
    const columnCount = getTableColumnCount(tableNode);
    if (columnCount <= 0) return false;

    // Get the table wrapper to determine available width
    const wrapper = view.dom.querySelector('.tableWrapper');
    const availableWidth = getAvailableTableWidth(wrapper);
    const minColumnWidth = 40;
    const targetWidth = availableWidth > 0
        ? Math.max(minColumnWidth * columnCount, availableWidth)
        : minColumnWidth * columnCount;
    const evenWidth = Math.max(minColumnWidth, Math.floor(targetWidth / columnCount));

    // Create equal widths for all columns
    const targetWidths = new Array(columnCount).fill(evenWidth);

    // Apply the widths using the existing function
    const result = applyTableColumnWidths(state.tr, tablePos, tableNode, targetWidths);
    if (!result.changed) return false;

    view.dispatch(result.tr);
    return true;
}

function applyDefaultTableColumnWidths(editor) {
    const context = getActiveTableContext(editor.state);
    if (!context) return false;

    const { state, view } = editor;
    const { tableNode, tablePos } = context;
    const columnCount = getTableColumnCount(tableNode);
    if (columnCount <= 0) return false;

    // Get the docs canvas to check width mode
    const docsCanvas = view.dom.closest('.docs-canvas');
    const isNarrowMode = docsCanvas?.dataset.width === 'narrow';
    const minColumnWidth = isNarrowMode ? 160 : 288;

    // Create default widths for all columns
    const targetWidths = new Array(columnCount).fill(minColumnWidth);

    // Apply the widths using the existing function
    const result = applyTableColumnWidths(state.tr, tablePos, tableNode, targetWidths);
    if (!result.changed) return false;

    view.dispatch(result.tr);
    return true;
}

function tableHasAutoNumberColumn(state) {
    const context = getActiveTableContext(state);
    if (!context) return false;

    const { tableNode } = context;
    if (!tableNode.childCount) return false;

    const firstRow = tableNode.child(0);
    if (!firstRow?.childCount) return false;

    const firstCellText = extractPlainText(firstRow.child(0)).trim();
    if (firstCellText === "#") return true;

    let numericCells = 0;
    let checkedCells = 0;
    tableNode.forEach((rowNode, rowOffset, rowIndex) => {
        if (!rowNode?.childCount) return;
        const text = extractPlainText(rowNode.child(0)).trim();
        if (!text) return;
        checkedCells += 1;
        const expected = String(rowIndex + 1);
        if (text === expected) numericCells += 1;
    });

    return checkedCells > 0 && numericCells === checkedCells;
}
